import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ScriptLoaderService } from 'src/app/services/script-loader.service';
import {
  shouldUseOzonetelSDK,
  useOzonetelCloudagentMdlForControls,
  shouldAutoAnswerOzonetelInbound,
  useDigitalConsoleSipForInboundAudio,
  useToolbarInboundPick,
  OZONETEL_INBOUND_AGENT_MODE,
  OZONETEL_MANUAL_DIAL_MODE,
} from './ozonetel-dialer-mode';
import { OzonetelCloudagentDirectService } from './ozonetel-cloudagent-direct.service';
import { OzonetelDialerFlowService } from './ozonetel-dialer-flow.service';

/**
 * Ozonetel SDK core — DigitalConsole integration, call lifecycle, and UI event bridge.
 *
 * ARCHITECTURE
 * - Calls/audio: DigitalConsole (dc) + JsSIP + optional OneCxi WebRTC.
 * - Hold/mute: CloudAgent MDL via server proxy when ozonetel_cloudagent_mdl is enabled.
 * - UI: CallingComponent subscribes to agentState$ and callEvent$ (not REST polling).
 *
 * CALL LIFECYCLE (one agent session, many legs)
 * 1) Login: dc.initialize() → parallel CloudAgent MDL session → setAgentInboundReady().
 * 2) Ring: DC WS events (onNewCall / onBusyAgent) or inbound poll detects ucid in getCallData().
 * 3) Answer: toolbar screen-pop (custData) → dc.sipAccept() for inbound SIP audio.
 * 4) Connected: emit NEW_CALL + BUSY → widget shows number/ucid/campaign and control buttons.
 * 5) Disconnect: handleCallEnded() → wrapUpPending + DROP → widget shows disposition panel.
 * 6) Disposition: agent selects from dialersDispositionList.json (S3) → dc.setDisposition() → completeWrapUpAfterDisposition().
 * 7) Re-arm: syncAgentIdleAfterCallEnd() — agent can receive the next call.
 * 8) Next call: lastEndedUcid blocks stale leg only; new ucid clears guards and re-syncs UI.
 *
 * WRAP-UP / DISPOSITION FLOW (Ozonetel SDK dialer only)
 * ─────────────────────────────────────────────────────
 * Call end (hangup, remote disconnect, watchdog) → handleCallEnded()
 *   → captureWrapUpCallDataSnapshot()  (monitorUcid, ucid, did, dataId for setDisposition)
 *   → wrapUpPending = true             (blocks onNewCall, manual dial, agent re-arm)
 *   → emitCallEvent('DROP')            (CallingComponent opens wrap-up UI)
 *   → deferRearmUntilWrapUp()          (no-op while wrapUpPending)
 *
 * CallingComponent (DROP handler) → enterOzonetelWrapUp()
 *   → Status = Disconnected, caller info kept visible
 *   → loadOzonetelDispositions()       (OzonetelWrapUpService → dialersDispositionList.json S3)
 *   → wrap timer starts (featureList.wrapDuration)
 *
 * Agent clicks Dispose (or timer → autoDisposeCall) → submitOzonetelDisposition()
 *   → OzonetelWrapUpService.submitDisposition()
 *   → submitWrapUpDisposition() → setDisposition() → dc.setDisposition(..., false)
 *   → completeWrapUpAfterDisposition() → wrapUpPending = false → syncAgentIdleAfterCallEnd()
 *
 * Files: ozonetel-sdk-core.service.ts | ozonetel-wrap-up.service.ts | dialersDispositionList.json
 *        calling.component.ts (UI) | calling.component.html (disposition panel)
 *
 * REMOTE DISCONNECT (customer hangs up)
 * DC may not fire onDropCall. We listen to:
 * - onAgentCallDisconnected, onAgentRelease, onDropCall, onCallDetails(endTime)
 * - SIP_CALL_ENDED / SIP_DISCONNECTED window events, callAudioElement ended
 * - Watchdog: getCallData() throws or returns ended/empty while callSessionActive
 *
 * SESSION GUARDS (prevent stale UI/audio between legs)
 * - lastEndedUcid: ignore DC/poll events for the leg that just ended.
 * - callSessionActive: true while a call is live (audio + UI should be connected).
 * - postCallCooldownUntil: short window to avoid false-positive disconnect from stale getCallData.
 *
 * INBOUND AUTO-ANSWER (SDK has no Accept/Decline UI — iframe CloudAgent does):
 * 1) POST /cloudagent/toolbar/pick-inbound → GET getCustomerInfo (custData / screen-pop).
 * 2) dc.sipAccept() — WebRTC answer (not answerSIPCall).
 *
 * MANUAL / OUTBOUND (AgentManualDial, dc.initManualCall, backend manual-dial):
 * - Ozonetel rings the agent SIP leg on onNewCall — must sipAccept() quickly or dropCall NoAnswer.
 * - Do not use OneCxi outbound stream when DC SIP is enabled; use autoAnswerOutboundSipCall().
 * - manualDialPendingUntil: watchdog grace while call data is still empty during connect.
 *
 * Ref: iframeHttpsCurls, docs/Ozonetel-SDK-CloudAgent-MDL-Integration.docx
 */

declare var DigitalConsole: any;
declare var OneCxi: any;

export interface OzonetelConfig {
  customer: string;
  agentId: string;
  password: string;
  phoneNumber?: string;
  userName?: string;
  sip?: boolean;
  /** From user profile — used when server login returns "already logged in". */
  usId?: string;
  /** From user profile — paired with usId for mdl payloads. */
  agentUniqId?: number;
}

export interface OzonetelAgentConfigResponse {
  resultShort: string;
  resultLong?: string;
  ozonetelDetails?: any;
}

export interface AgentState {
  initialized: boolean;
  modeSet: boolean;
  loggedIn: boolean;
  status: string;
  mode?: string;
}

export interface CallEvent {
  type: string;
  data?: any;
}

// OneCxi SDK credentials - fetched from backend
let ONE_CXI_API_KEY: string | null = null;

@Injectable({
  providedIn: 'root'
})
export class OzonetelSDKCoreService {
  private dc: any = null;
  private oneCXI: any = null;
  private config: OzonetelConfig | null = null;
  private oneCxiInitialized = false;
  private isStreaming = false;
  private oneCxiDidNumbers: string[] = [];
  private oneCxiPreferredDid = '';
  private useSDK = shouldUseOzonetelSDK();
  private sdkLoaded = false;
  /** Merged onNewCall / onBusyAgent / onCallDetails — used for mdl ucid, campaign, phones. */
  private latestCallSignals: any = null;
  /** Dedupes parallel server /cloudagent/login during init. */
  private cloudagentLoginPromise: Promise<boolean> | null = null;
  /** Blocks repeated /cloudagent/login after a failure until cooldown expires. */
  private cloudagentLoginCooldownUntil = 0;
  private cloudagentLoginBlocked = false;
  /** True after server /cloudagent/login or sync-session established server-side MDL map. */
  private serverMdlSessionReady = false;
  /** Stops hammering pick-inbound when server session cannot be established. */
  private inboundToolbarPickBlocked = false;
  private inboundToolbarPickAttempts = 0;
  /** Short-lived cache for GET /agent-config to avoid hammering the server. */
  private agentConfigCache: { at: number; data: OzonetelAgentConfigResponse | null } | null = null;
  /** Last dc.initialize() payload — scanned for usId / alreadyLoggedInMap. */
  private lastSdkInitResult: unknown = null;
  /** Per-ucid lock so parallel auto-answer retries do not block a new ringing leg. */
  private inboundAutoAnswerUcids = new Set<string>();
  /** Safety net when DC events are late — polls getCallData while agent is IDLE/INBOUND. */
  private inboundCallPollTimer: ReturnType<typeof setInterval> | null = null;
  /** Polls getCallData every 500ms during active/busy legs — remote hangup + missed UI sync. */
  private callStateWatchdogTimer: ReturnType<typeof setInterval> | null = null;
  private watchdogHandling = false;
  private lastToolbarPickUcid = '';
  /** UCID of the last ended leg — blocks resurrecting stale call data after disconnect. */
  private lastEndedUcid = '';
  /** UCID of the current live leg — used for remote-hangup detection. */
  private activeCallUcid = '';
  /** True from connect (NEW_CALL/BUSY) until handleCallEnded — drives disconnect detection. */
  private callSessionActive = false;
  /** Brief window after disconnect where stale getCallData should not re-trigger disconnect. */
  private postCallCooldownUntil = 0;
  /** True while syncAgentIdleAfterCallEnd runs — ignore onAgentRelease during re-arm. */
  private rearmInProgress = false;
  /** True after disconnect until agent submits disposition via setDisposition. */
  private wrapUpPending = false;
  /** Snapshot of getCallData for wrap-up — SDK setDisposition reads monitorUcid/dataId from here. */
  private wrapUpCallDataSnapshot: any = null;
  /** SIP leg cleanup deferred until disposition completes. */
  private pendingCallEndCleanup = false;
  /** One-shot transfer-release bypass consumed on the next matching disconnect. */
  private skipNextWrapUpForTransferRelease = false;
  /** Expiry for one-shot transfer bypass to avoid stale carryover. */
  private skipWrapUpForTransferUntil = 0;
  /** Optional UCID guard so bypass applies only to the transferred leg. */
  private skipWrapUpForTransferUcid = '';
  private lastCallEndAt = 0;
  private callConnectGraceUntil = 0;
  /** While set, manual dial is ringing/connecting — watchdog must not treat empty getCallData as hangup. */
  private manualDialPendingUntil = 0;
  private previousEmittedStatus = 'UNKNOWN';
  private inboundConnectUnholdKeys = new Set<string>();
  private callTeardownInFlight = false;
  private defaultHandlersRegistered = false;
  private audioDisconnectListenerBound = false;
  /** OneCxi app_to_app WebCall → Accept listener (non–DC-SIP inbound only). */
  private oneCxiWebCallListenerBound = false;
  private agentState: AgentState = {
    initialized: false,
    modeSet: false,
    loggedIn: false,
    status: 'UNKNOWN',
    mode: 'UNKNOWN'
  };

  // Observables for state management - emit state changes
  private agentStateSubject = new BehaviorSubject<AgentState>(this.agentState);
  public agentState$: Observable<AgentState> = this.agentStateSubject.asObservable();

  private callEventSubject = new BehaviorSubject<CallEvent>({ type: 'NONE' });
  public callEvent$: Observable<CallEvent> = this.callEventSubject.asObservable();

  constructor(
    private http: HttpClient,
    private scriptLoader: ScriptLoaderService,
    /** MDL session + server-proxied hold/mute (see ozonetel-cloudagent-direct.service.ts). */
    private cloudagentDirect: OzonetelCloudagentDirectService,
    private dialerFlow: OzonetelDialerFlowService
  ) {
    console.log('[OzonetelSDKCore] Constructor called, useSDK:', this.useSDK);
    if (this.useSDK) {
      this.initializeSDK();
      this.fetchApiKey();
    }
  }

  private async fetchApiKey(): Promise<void> {
    if (!this.useSDK) {
      console.log('[OzonetelSDKCore] SDK disabled, skipping API key fetch');
      return;
    }

    if (ONE_CXI_API_KEY) {
      return;
    }

    try {
      const result: any = await firstValueFrom(
        this.http.get(`${environment.api_url}/api/ozonetel/config/api-key`)
      );

      if (result.resultShort === 'success' && result.apiKey) {
        ONE_CXI_API_KEY = result.apiKey;
        console.log('[OzonetelSDKCore] API key fetched from backend');
      }
    } catch (error) {
      console.error('[OzonetelSDKCore] Failed to fetch API key from backend:', error);
    }
  }

  /**
   * Pushes agent state to CallingComponent. Also catches BUSY→IDLE transitions that
   * occur without onDropCall (e.g. remote hangup) while callSessionActive is still true.
   */
  private emitAgentState(): void {
    const prev = this.previousEmittedStatus;
    const next = this.agentState.status;
    if (
      prev === 'BUSY' &&
      next === 'IDLE' &&
      this.callSessionActive &&
      !this.rearmInProgress &&
      !this.isManualDialPending()
    ) {
      // Skip while manual dial is connecting — BUSY→IDLE can be transient before sipAccept.
      console.log('[OzonetelSDKCore] BUSY→IDLE while call session active — syncing disconnect');
      this.handleCallEnded({ source: 'DigitalConsole', event: 'agentIdleTransition' });
      return;
    }
    this.previousEmittedStatus = next;
    this.agentStateSubject.next({ ...this.agentState });
  }

  /**
   * Emits call events to the calling widget via callEvent$.
   * NEW_CALL / BUSY / INBOUND_SCREEN_POP start a session; DROP ends it.
   */
  private emitCallEvent(type: string, data?: any): void {
    if (type === 'DROP') {
      this.callEventSubject.next({ type, data: data || {} });
      return;
    }
    if (type === 'NEW_CALL' || type === 'BUSY' || type === 'INBOUND_SCREEN_POP') {
      this.clearEndedCallGuardsForNewLeg(this.extractUcid(data));
      this.callSessionActive = true;
      this.bindCallAudioDisconnectListener();
    }
    const merged = { ...(this.latestCallSignals || {}), ...(data || {}) };
    if (!this.hasToolbarCustData(merged.custData) && this.hasToolbarCustData(this.latestCallSignals?.custData)) {
      merged.custData = this.latestCallSignals.custData;
    }
    const enriched: any = this.dialerFlow.enrichCallDisplayData(merged);
    if (this.isTransferLegData(enriched)) {
      enriched.transferCall = true;
    }
    this.latestCallSignals = { ...(this.latestCallSignals || {}), ...enriched };
    if (
      (type === 'BUSY' || type === 'INBOUND_SCREEN_POP') &&
      enriched.transferCall
    ) {
      void this.autoUnholdTransferLeg(enriched);
    }
    if (
      (type === 'BUSY' || type === 'INBOUND_SCREEN_POP') &&
      (this.isInboundSdkCall(enriched) || this.agentState.mode === OZONETEL_INBOUND_AGENT_MODE)
    ) {
      this.scheduleInboundConnectUnhold(enriched, 'busy');
    }
    this.callEventSubject.next({ type, data: enriched });
  }

  private hasToolbarCustData(custData: unknown): boolean {
    if (custData == null || custData === '') {
      return false;
    }
    if (Array.isArray(custData)) {
      return custData.length > 0;
    }
    return true;
  }

  private isTransferLegData(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }
    if (data.transferCall === true || data.isTransfer === true || data.transfer === true) {
      return true;
    }
    const type = String(
      data.callType ?? data.calltype ?? data.type ?? data.mode ?? data.transferType ?? ''
    ).toLowerCase();
    if (type.includes('transfer')) {
      return true;
    }
    const status = String(data.status ?? data.callStatus ?? data.callState ?? '').toLowerCase();
    return status.includes('transfer');
  }

  private async autoUnholdTransferLeg(callData?: any): Promise<void> {
    this.scheduleInboundConnectUnhold(callData, 'transfer');
  }

  async forceUnholdActiveCall(): Promise<{ success: boolean; source?: string }> {
    if (this.dc && typeof this.dc.unHoldCall === 'function') {
      try {
        const dcResult = await this.dc.unHoldCall();
        const dcOk = dcResult?.status === 'Success' || dcResult?.resp === 'Success';
        if (dcOk) {
          return { success: true, source: 'dc' };
        }
      } catch (e) {
        console.warn('[OzonetelSDKCore] DC unHoldCall failed:', e);
      }
    }

    if (useOzonetelCloudagentMdlForControls()) {
      const mdl = await this.invokeCloudagentMdl('unhold');
      if (mdl.success) {
        return { success: true, source: 'mdl' };
      }
    }

    if (this.oneCXI && this.isStreaming) {
      try {
        this.oneCXI.toggleHold();
        return { success: true, source: 'onecxi' };
      } catch (e) {
        console.warn('[OzonetelSDKCore] OneCxi unhold failed:', e);
      }
    }

    return { success: false };
  }

  private scheduleInboundConnectUnhold(callData?: any, reason = 'inbound'): void {
    const key = this.extractUcid(callData) || reason;
    if (this.inboundConnectUnholdKeys.has(key)) {
      return;
    }
    this.inboundConnectUnholdKeys.add(key);
    void (async () => {
      try {
        await new Promise((r) => setTimeout(r, 1500));
        for (let i = 0; i < 8; i++) {
          const result = await this.forceUnholdActiveCall();
          if (result.success) {
            console.log('[OzonetelSDKCore] Inbound connect unhold OK', {
              key,
              attempt: i + 1,
              source: result.source,
            });
            return;
          }
          if (i < 7) {
            await new Promise((r) => setTimeout(r, 700 + i * 350));
          }
        }
        console.warn('[OzonetelSDKCore] Inbound connect unhold exhausted retries', { key });
      } finally {
        setTimeout(() => this.inboundConnectUnholdKeys.delete(key), 90000);
      }
    })();
  }

  private isCallAlreadyEndedMessage(msg: string): boolean {
    return /not found|no active|no call|already|invalid|disconnect/i.test(msg);
  }

  private isInPostCallCooldown(): boolean {
    return Date.now() < this.postCallCooldownUntil;
  }

  private extractUcid(data: any): string {
    return String(data?.ucid || data?.monitorUcid || data?.UCID || '').trim();
  }

  // --- Call session guards: distinguish a new leg from stale data of the previous leg ---

  private isNewCallUcid(ucid: string): boolean {
    return !!ucid && ucid !== this.lastEndedUcid;
  }

  /** Clears lastEndedUcid when a genuinely new ucid arrives so the next call can sync UI. */
  private clearEndedCallGuardsForNewLeg(ucid?: string): void {
    const id = ucid || '';
    if (!id || this.isNewCallUcid(id)) {
      this.lastEndedUcid = '';
      this.postCallCooldownUntil = 0;
    }
  }

  /** True when getCallData has a live leg (no endTime, valid ucid, not the just-ended ucid). */
  private isCallDataActive(cd: any): boolean {
    if (!cd || typeof cd !== 'object' || cd.endTime) {
      return false;
    }
    const ucid = this.extractUcid(cd);
    if (!this.isNewCallUcid(ucid)) {
      return false;
    }
    const status = String(cd.status || cd.callStatus || cd.callState || cd.agentStatus || '').toLowerCase();
    if (/disconnect|ended|complete|idle|drop|hang|wrap/i.test(status)) {
      return false;
    }
    return true;
  }

  /** Gate for promoting a call leg to active UI — blocks stale ucid from the previous disconnect. */
  private canPromoteCallToActive(ucid: string): boolean {
    return this.isNewCallUcid(ucid);
  }

  /** Filters DC WS events — rejects events for the ucid that just ended. */
  private acceptDcCallEvent(data: any): boolean {
    const ucid = this.extractUcid(data);
    if (!ucid) {
      return true;
    }
    if (ucid === this.lastEndedUcid) {
      console.log('[OzonetelSDKCore] Ignoring DC event for ended ucid', ucid);
      return false;
    }
    if (this.lastEndedUcid) {
      this.lastEndedUcid = '';
      this.postCallCooldownUntil = 0;
    }
    return true;
  }

  private isInCallConnectGrace(): boolean {
    return Date.now() < this.callConnectGraceUntil;
  }

  private isManualDialPending(): boolean {
    return Date.now() < this.manualDialPendingUntil;
  }

  /** Called before backend/SDK manual dial — extends grace so early empty call data does not DROP the leg. */
  private beginManualDialAttempt(): void {
    const now = Date.now();
    this.manualDialPendingUntil = now + 30000;
    this.callConnectGraceUntil = Math.max(this.callConnectGraceUntil, now + 15000);
    this.rearmInProgress = false;
    this.postCallCooldownUntil = 0;
  }

  private clearManualDialPending(): void {
    this.manualDialPendingUntil = 0;
  }

  cancelManualDialAttempt(): void {
    this.clearManualDialPending();
  }

  /** Records the current live leg and attaches audio-ended listener for remote hangup detection. */
  private markActiveCall(data?: any): void {
    const ucid = this.extractUcid(data) || this.extractUcid(this.latestCallSignals);
    if (ucid) {
      this.activeCallUcid = ucid;
    }
    if (!this.isOutboundSdkLeg(data)) {
      this.clearManualDialPending();
    }
    this.callSessionActive = true;
    this.callConnectGraceUntil = Date.now() + 6000;
    this.bindCallAudioDisconnectListener();
  }

  isCallSessionActive(): boolean {
    return this.callSessionActive;
  }

  isLiveCallData(cd: any): boolean {
    return this.isCallDataActive(cd);
  }

  isManualDialConnectPending(): boolean {
    return this.isManualDialPending();
  }

  private async waitForRearmComplete(maxMs = 5000): Promise<void> {
    const start = Date.now();
    while (this.rearmInProgress && Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 100));
    }
  }

  private hasActiveCallSession(): boolean {
    return (
      this.callSessionActive ||
      this.agentState.status === 'BUSY' ||
      !!this.activeCallUcid ||
      !!this.extractUcid(this.latestCallSignals) ||
      this.isStreaming
    );
  }

  /**
   * Returns true when getCallData indicates the remote party hung up
   * (empty data, endTime, missing ucid, or disconnected status fields).
   */
  private isRemoteCallEnded(cd: any): boolean {
    if (this.isInCallConnectGrace() || this.isManualDialPending()) {
      return false;
    }
    if (!this.hasActiveCallSession()) {
      return false;
    }
    const tracked = this.activeCallUcid || this.extractUcid(this.latestCallSignals);
    if (!tracked) {
      return false;
    }
    if (!cd || typeof cd !== 'object') {
      return true;
    }
    if (cd.endTime) {
      return true;
    }
    const ucid = this.extractUcid(cd);
    if (!ucid) {
      return false;
    }
    const status = String(
      cd.status || cd.callStatus || cd.callState || cd.agentStatus || ''
    ).toLowerCase();
    if (/disconnect|ended|complete|drop|hang|wrap|release|not.?ready/i.test(status)) {
      return true;
    }
    return false;
  }

  /** Entry point for DC remote-disconnect WS events (customer hangup, agent release). */
  private handleRemoteDisconnect(data: any, event: string): void {
    if (!this.callSessionActive && !this.hasActiveCallSession()) {
      if (this.agentState.status === 'BUSY') {
        this.agentState.status = 'IDLE';
        this.emitAgentState();
      }
      return;
    }
    void this.teardownActiveCall(data, event);
  }

  private async teardownActiveCall(data: any, event: string): Promise<void> {
    if (this.callTeardownInFlight) {
      return;
    }
    this.callTeardownInFlight = true;
    try {
      if (this.oneCXI && this.isStreaming) {
        try {
          this.oneCXI.stopStreaming();
          this.isStreaming = false;
        } catch (e) {
          console.warn('[OzonetelSDKCore] Stop stream during teardown:', e);
        }
      }
      await this.cleanupDcSipLegOnCallEnd();
    } finally {
      this.callTeardownInFlight = false;
    }
    this.handleCallEnded({
      ...(data || {}),
      source: 'DigitalConsole',
      event,
    });
  }

  private shouldSkipLegCleanupOnEnd(data: any): boolean {
    return (
      data?.alreadyEnded === true ||
      data?.event === 'onDropCall' ||
      (data?.source === 'OneCxi' &&
        data?.event !== 'Ended' &&
        data?.event !== 'Disconnected' &&
        data?.event !== 'callTerminated')
    );
  }

  /**
   * SIP inbound audio uses dc.callAudioElement. When the stream ends (remote hangup),
   * the ended/emptied events fire even if DC WS events are delayed.
   */
  private bindCallAudioDisconnectListener(): void {
    const attach = (): boolean => {
      const el = this.dc?.callAudioElement || (window as any).dc?.callAudioElement;
      if (!el || (el as any)._ozonetelDisconnectBound) {
        return !!(el as any)?._ozonetelDisconnectBound;
      }
      const onAudioEnd = () => {
        if (!this.callSessionActive || this.isInCallConnectGrace()) {
          return;
        }
        console.log('[OzonetelSDKCore] callAudioElement media ended');
        this.handleCallEnded({ source: 'DigitalConsole', event: 'callAudioEnded' });
      };
      el.addEventListener('ended', onAudioEnd);
      (el as any)._ozonetelDisconnectBound = true;
      return true;
    };
    if (!attach() && !this.audioDisconnectListenerBound) {
      setTimeout(() => attach(), 1500);
    }
    this.audioDisconnectListenerBound = true;
  }

  async getAgentConfigFromDB(): Promise<OzonetelAgentConfigResponse | null> {
    const now = Date.now();
    if (this.agentConfigCache && now - this.agentConfigCache.at < 30000) {
      return this.agentConfigCache.data;
    }
    try {
      const response = await firstValueFrom(
        this.http.get<OzonetelAgentConfigResponse>(`${environment.api_url}/api/ozonetel/agent-config`)
      );
      this.agentConfigCache = { at: now, data: response || null };
      return response || null;
    } catch (error) {
      console.error('[OzonetelSDKCore] Failed to fetch Ozonetel agent config from DB:', error);
      this.agentConfigCache = { at: now, data: null };
      return null;
    }
  }

  private async ensureSDKLoaded(): Promise<void> {
    if (!this.useSDK) {
      return;
    }

    if (this.sdkLoaded) {
      return;
    }

    await this.scriptLoader.loadOzonetelSDK();
    this.sdkLoaded = true;

    await new Promise(resolve => setTimeout(resolve, 500));
  }

  private initializeSDK(): void {
    if (typeof DigitalConsole === 'undefined') {
      console.error('[OzonetelSDKCore] DigitalConsole SDK not loaded');
      return;
    }

    if (!(window as any).dc) {
      console.log('[OzonetelSDKCore] Creating DigitalConsole instance');
      (window as any).dc = new DigitalConsole();
      this.dc = (window as any).dc;
      console.log('[OzonetelSDKCore] DigitalConsole instance created');
    } else {
      this.dc = (window as any).dc;
    }
  }

  async logoutAgent(customer: string, agentId: string): Promise<boolean> {
    if (!this.dc) {
      console.error('[OzonetelSDKCore] Cannot logout - SDK not initialized');
      return false;
    }

    console.log('[OzonetelSDKCore] Logging out agent', { customer, agentId });

    try {
      // End CloudAgent mdl session (tbAgentLogout) before SDK logout when hybrid MDL is on
      if (useOzonetelCloudagentMdlForControls()) {
        try {
          const snap = await this.buildMdlSnapshot();
          await this.cloudagentDirect.logout(snap?.agentPhoneForMute || '');
        } catch {
        }
      }

      const result = await this.dc.logout(customer, agentId);
      const isSuccess = (result && result.resp === 'Success') ||
        (typeof result === 'string' && result.includes('Success'));

      if (isSuccess) {
        console.log('[OzonetelSDKCore] Agent logged out successfully');
        this.stopInboundCallPoll();
        this.stopCallStateWatchdog();
        this.postCallCooldownUntil = 0;
        this.rearmInProgress = false;
        this.callSessionActive = false;
        this.wrapUpPending = false;
        this.wrapUpCallDataSnapshot = null;
        this.pendingCallEndCleanup = false;
        this.activeCallUcid = '';
        this.lastEndedUcid = '';
        // Reset transfer bypass guards on logout to avoid leaking session state.
        this.skipNextWrapUpForTransferRelease = false;
        this.skipWrapUpForTransferUntil = 0;
        this.skipWrapUpForTransferUcid = '';
        this.previousEmittedStatus = 'DISCONNECTED';
        this.inboundAutoAnswerUcids.clear();
        this.agentState.loggedIn = false;
        this.agentState.initialized = false;
        this.agentState.status = 'DISCONNECTED';
        this.latestCallSignals = null;
        this.cloudagentLoginBlocked = false;
        this.cloudagentLoginCooldownUntil = 0;
        this.inboundToolbarPickBlocked = false;
        this.inboundToolbarPickAttempts = 0;
        this.agentConfigCache = null;
        this.callEventSubject.next({ type: 'NONE' });
        this.emitAgentState();
        return true;
      } else {
        console.warn('[OzonetelSDKCore] Logout returned unexpected response', result);
        return false;
      }
    } catch (error) {
      console.error('[OzonetelSDKCore] Error during logout', error);
      return false;
    }
  }

  private async mergeConfigFromDb(config: OzonetelConfig): Promise<OzonetelConfig> {
    try {
      const dbConfig = await this.getAgentConfigFromDB();
      const d = dbConfig?.ozonetelDetails;
      if (!d) {
        return config;
      }
      const dbUsId = d.usId ?? d.usid;
      const dbAgentUniqId =
        d.agentUniqId != null
          ? Number(d.agentUniqId)
          : d.agentuniqId != null
            ? Number(d.agentuniqId)
            : undefined;
      return {
        ...config,
        customer: config.customer || d.customer || d.userName,
        agentId: config.agentId || d.agentId,
        password: config.password || d.pin || d.password,
        phoneNumber: config.phoneNumber || d.phoneNumber || '',
        userName: config.userName || d.userName,
        usId: config.usId || dbUsId,
        agentUniqId:
          config.agentUniqId != null && Number.isFinite(Number(config.agentUniqId))
            ? Number(config.agentUniqId)
            : dbAgentUniqId != null && Number.isFinite(dbAgentUniqId)
              ? dbAgentUniqId
              : config.agentUniqId,
      };
    } catch {
      return config;
    }
  }

  /**
   * Agent login: dc.initialize() → MDL session → setAgentInboundReady() → start watchdog/poll.
   * Registers DC WS handlers once via setupDefaultEventHandlers().
   */
  async initializeAgent(config: OzonetelConfig, retryAfterLogout: boolean = false): Promise<any> {
    config = await this.mergeConfigFromDb(config);
    this.config = config;

    if (!this.useSDK) {
      console.log('[OzonetelSDKCore] SDK disabled - agent initialization skipped');
      console.log('[OzonetelSDKCore] Set environment.ozonetel_sdk = true to enable SDK');
      this.agentState.initialized = false;
      this.agentState.loggedIn = false;
      this.agentState.status = 'SDK_DISABLED';
      this.agentState.mode = 'NONE';
      this.emitAgentState();
      return {
        resultShort: 'skipped',
        resultLong: 'SDK disabled in environment configuration',
        sdkEnabled: false
      };
    }

    console.log('[OzonetelSDKCore] SDK enabled, proceeding with initialization');

    await this.ensureSDKLoaded();

    if (!this.dc) {
      this.initializeSDK();
      if (!this.dc) {
        throw new Error('DigitalConsole SDK not available');
      }
    }

    console.log('[OzonetelSDKCore] Initializing agent', {
      customer: config.customer,
      agentId: config.agentId,
      sip: config.sip !== false
    });

    const initParams = {
      customer: config.customer,
      agentid: config.agentId,
      phoneNumber: config.phoneNumber || '',
      pin: config.password,
      sip: config.sip !== false
    };

    const hybrid = useOzonetelCloudagentMdlForControls();
    this.config = config;

    try {
      const result = await this.dc.initialize(initParams);
      console.log('[OzonetelSDKCore] Initialize result:', result);

      if (result?.status === 'error' && result?.message?.includes('already logged in')) {
        console.warn('[OzonetelSDKCore] Agent already logged in detected', {
          message: result.message,
          alreadyLoggedInMap: result.alreadyLoggedInMap
        });

        if (!retryAfterLogout) {
          console.log('[OzonetelSDKCore] Attempting logout and retry...');
          const loggedOut = await this.logoutAgent(config.customer, config.agentId);

          if (loggedOut) {
            console.log('[OzonetelSDKCore] Waiting 1 second before retry...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            return await this.initializeAgent(config, true);
          } else {
            console.warn('[OzonetelSDKCore] Logout failed, but proceeding with initialization anyway');
            await new Promise(resolve => setTimeout(resolve, 2000));
            return await this.initializeAgent(config, true);
          }
        } else {
          console.warn('[OzonetelSDKCore] Still getting already logged in after logout, but checking if we can proceed');
          const hasLoggedInField = result.loggedIn === 'true' || result.loggedIn === true;
          const hasSuccessOnly = result.success === true;

          if (hasLoggedInField || hasSuccessOnly) {
            console.log('[OzonetelSDKCore] Proceeding despite already logged in error');
            this.agentState.initialized = true;
            this.agentState.loggedIn = true;
            this.agentState.status = 'NOT_READY';
            this.agentState.mode = OZONETEL_INBOUND_AGENT_MODE;
            this.emitAgentState();

            const oneCxiUserName = config.userName || config.customer;

            if (config.sip !== false && oneCxiUserName) {
              await this.initializeOneCxi(oneCxiUserName);
            }

            this.setupDefaultEventHandlers();

            await this.establishCloudagentMdlSession(config, result, hybrid);
            await this.setAgentInboundReady();

            return result;
          } else {
            throw new Error('Agent still logged in after logout attempt');
          }
        }
      }

      const loginSuccess = result?.loggedIn === 'true' ||
        result?.loggedIn === true ||
        result?.success === true;

      if (!loginSuccess) {
        throw new Error('Login failed: ' + JSON.stringify(result));
      }

      this.lastSdkInitResult = result;

      this.agentState.initialized = true;
      this.agentState.loggedIn = true;
      this.agentState.status = 'NOT_READY';
      this.agentState.mode = OZONETEL_INBOUND_AGENT_MODE;

      console.log('[OzonetelSDKCore] Agent logged in successfully', {
        agentKey: result?.agentKey ? 'present' : 'missing',
        agentUserName: result?.agentUserName
      });

      // OneCxi uses customer name as userName and hardcoded API key (same as ozonetel-test)
      const oneCxiUserName = config.userName || config.customer;

      if (config.sip !== false && oneCxiUserName) {
        await this.initializeOneCxi(oneCxiUserName);
      }

      this.setupDefaultEventHandlers();

      await this.establishCloudagentMdlSession(config, result, hybrid);

      await this.setAgentInboundReady();
      this.emitAgentState();

      return result;
    } catch (error) {
      console.error('[OzonetelSDKCore] Initialization error:', error);
      this.agentState.initialized = false;
      this.agentState.loggedIn = false;
      throw error;
    }
  }

  /**
   * Recursively find usId and agentUniqId in SDK JSON (incl. agentKey, alreadyLoggedInMap).
   */
  private extractMdlIdentityFromPayload(
    payload: unknown
  ): { usId: string | null; agentUniqId: number | null } {
    if (payload == null) {
      return { usId: null, agentUniqId: null };
    }
    const visit = (v: unknown, depth: number): { usId: string | null; agentUniqId: number | null } => {
      if (depth > 4 || v == null) {
        return { usId: null, agentUniqId: null };
      }
      if (typeof v === 'string') {
        try {
          return visit(JSON.parse(v), depth + 1);
        } catch {
          const usM = v.match(/"usId"\s*:\s*"([^"]+)"/);
          const agM = v.match(/"agentUniqId"\s*:\s*([0-9]+)/);
          return {
            usId: usM ? usM[1] : null,
            agentUniqId: agM ? Number(agM[1]) : null,
          };
        }
      }
      if (typeof v !== 'object') {
        return { usId: null, agentUniqId: null };
      }
      const o = v as Record<string, unknown>;
      let usId =
        o['usId'] != null
          ? String(o['usId'])
          : o['UsId'] != null
            ? String(o['UsId'])
            : o['userSessionId'] != null
              ? String(o['userSessionId'])
              : o['sessionId'] != null
                ? String(o['sessionId'])
                : o['agentKey'] != null
                  ? String(o['agentKey'])
                  : null;
      let agentUniqId =
        o['agentUniqId'] != null
          ? Number(o['agentUniqId'])
          : o['AgentUniqId'] != null
            ? Number(o['AgentUniqId'])
            : o['agentUniqueId'] != null
              ? Number(o['agentUniqueId'])
              : null;
      if (usId && Number.isFinite(agentUniqId)) {
        return { usId, agentUniqId };
      }
      for (const key of Object.keys(o)) {
        const nested = visit(o[key], depth + 1);
        usId = usId || nested.usId;
        if (nested.agentUniqId != null && Number.isFinite(nested.agentUniqId)) {
          agentUniqId = nested.agentUniqId;
        }
        if (usId && agentUniqId != null && Number.isFinite(agentUniqId)) {
          return { usId, agentUniqId };
        }
      }
      return { usId, agentUniqId: Number.isFinite(agentUniqId) ? agentUniqId : null };
    };
    return visit(payload, 0);
  }

  /** Collect mdl identity from SDK, call data, config, and environment fallbacks. */
  private async resolveMdlIdentity(sdkResult?: unknown): Promise<{ usId: string | null; agentUniqId: number | null }> {
    let cd: unknown = null;
    try {
      if (this.dc && typeof this.dc.getCallData === 'function') {
        cd = await this.dc.getCallData();
      }
    } catch {
    }
    const initMap =
      sdkResult && typeof sdkResult === 'object' && (sdkResult as Record<string, unknown>)['alreadyLoggedInMap'];
    const sources = [
      sdkResult,
      initMap,
      this.lastSdkInitResult,
      this.lastSdkInitResult &&
      typeof this.lastSdkInitResult === 'object' &&
      (this.lastSdkInitResult as Record<string, unknown>)['alreadyLoggedInMap'],
      cd,
      this.latestCallSignals,
    ];
    let usId: string | null = null;
    let agentUniqId: number | null = null;
    for (const src of sources) {
      const f = this.extractMdlIdentityFromPayload(src);
      usId = usId || f.usId;
      if (f.agentUniqId != null && Number.isFinite(f.agentUniqId)) {
        agentUniqId = f.agentUniqId;
      }
    }
    if (!usId && this.config?.usId) {
      usId = String(this.config.usId);
    }
    if ((agentUniqId == null || !Number.isFinite(agentUniqId)) && this.config?.agentUniqId != null) {
      agentUniqId = Number(this.config.agentUniqId);
    }
    return { usId, agentUniqId };
  }

  /** Apply usId/agentUniqId from OzonetelConfig (user profile / calling widget). */
  private seedMdlSessionFromConfigHints(): boolean {
    if (!this.config) {
      return false;
    }
    const usId = this.config.usId != null ? String(this.config.usId) : '';
    const agentUniqId =
      this.config.agentUniqId != null ? Number(this.config.agentUniqId) : NaN;
    if (!usId || !Number.isFinite(agentUniqId)) {
      return false;
    }
    this.cloudagentDirect.setSession({
      customer: this.config.customer,
      agentId: this.config.agentId,
      usId,
      agentUniqId,
    });
    return true;
  }

  /**
   * Resolve mdl session for hold/mute without calling /cloudagent/login again.
   * Order: memory → localStorage → profile → SDK/call data.
   */
  private async ensureMdlSessionForControls(): Promise<boolean> {
    if (this.cloudagentDirect.hasSession()) {
      return true;
    }
    if (!this.config) {
      return false;
    }
    if (this.cloudagentDirect.loadPersistedSession(this.config.customer, this.config.agentId)) {
      return true;
    }
    if (this.seedMdlSessionFromConfigHints()) {
      return true;
    }
    if (await this.seedMdlSessionFromSdk()) {
      return true;
    }
    return false;
  }

  private async ensureMdlSessionForInboundPick(): Promise<boolean> {
    if (this.inboundToolbarPickBlocked) {
      return this.serverMdlSessionReady;
    }
    if (this.serverMdlSessionReady && this.cloudagentDirect.hasSession()) {
      return true;
    }
    if (!this.config) {
      return false;
    }
    this.config = await this.mergeConfigFromDb(this.config);
    this.seedMdlSessionFromConfigHints();
    if (this.cloudagentDirect.hasSession()) {
      await this.cloudagentDirect.syncSessionToServer();
    }
    if (this.cloudagentLoginBlocked || Date.now() < this.cloudagentLoginCooldownUntil) {
      return this.serverMdlSessionReady;
    }
    return this.ensureCloudagentMdlSessionViaServer(false);
  }

  /** Apply mdlSession from POST /cloudagent/login success response. */
  private applyServerMdlSession(res: {
    mdlSession?: {
      customer?: string;
      agentId?: string;
      usId?: string;
      agentUniqId?: number;
      browserSessionId?: string;
      utid?: number;
    };
  }): boolean {
    const m = res?.mdlSession;
    if (!m?.usId || m.agentUniqId == null || !Number.isFinite(Number(m.agentUniqId))) {
      return false;
    }
    this.cloudagentDirect.setSession({
      customer: m.customer || this.config?.customer || '',
      agentId: m.agentId || this.config?.agentId || '',
      usId: String(m.usId),
      agentUniqId: Number(m.agentUniqId),
      browserSessionId: m.browserSessionId,
      utid: m.utid,
    });
    if (this.config) {
      this.config.usId = String(m.usId);
      this.config.agentUniqId = Number(m.agentUniqId);
    }
    this.serverMdlSessionReady = true;
    this.inboundToolbarPickBlocked = false;
    return true;
  }

  /**
   * Server proxy to auth_service/login. Used at agent init (not on each hold/mute click).
   * forceRelogin: passed when SDK slot may block login until cleared.
   */
  private async ensureCloudagentMdlSessionViaServer(forceRelogin = false): Promise<boolean> {
    if (this.cloudagentLoginBlocked) {
      return false;
    }
    if (Date.now() < this.cloudagentLoginCooldownUntil) {
      return false;
    }
    if (
      !forceRelogin &&
      this.serverMdlSessionReady &&
      this.cloudagentDirect.hasSession()
    ) {
      return true;
    }
    if (!this.config?.customer || !this.config?.agentId || !this.config?.password) {
      return false;
    }
    if (this.cloudagentLoginPromise) {
      return this.cloudagentLoginPromise;
    }
    this.cloudagentLoginPromise = (async () => {
      const { usId, agentUniqId } = await this.resolveMdlIdentity();
      if (this.cloudagentDirect.hasSession()) {
        await this.cloudagentDirect.syncSessionToServer();
      }
      try {
        const res: {
          resultShort?: string;
          resultLong?: string;
          mdlSession?: {
            customer?: string;
            agentId?: string;
            usId?: string;
            agentUniqId?: number;
            browserSessionId?: string;
            utid?: number;
          };
        } = await firstValueFrom(
          this.http.post(`${environment.api_url}/api/ozonetel/cloudagent/login`, {
            customer: this.config!.customer,
            agentId: this.config!.agentId,
            pin: this.config!.password,
            phoneNumber: this.config!.phoneNumber || '',
            forceRelogin,
            ...(usId ? { usId } : {}),
            ...(agentUniqId != null && Number.isFinite(agentUniqId) ? { agentUniqId } : {}),
          })
        );
        if (res?.resultShort === 'success' && this.applyServerMdlSession(res)) {
          this.cloudagentLoginBlocked = false;
          this.cloudagentLoginCooldownUntil = 0;
          this.agentConfigCache = null;
          console.log('[OzonetelSDKCore] CloudAgent mdl session ready (auth_service/login via server)');
          return true;
        }
        if (this.serverMdlSessionReady && this.cloudagentDirect.hasSession()) {
          return true;
        }
        this.cloudagentLoginCooldownUntil = Date.now() + 60000;
        console.warn('[OzonetelSDKCore] CloudAgent mdl login:', res?.resultLong);
        return false;
      } catch (e: unknown) {
        this.cloudagentLoginCooldownUntil = Date.now() + 60000;
        console.error('[OzonetelSDKCore] CloudAgent mdl login request failed', e);
        return this.serverMdlSessionReady && this.cloudagentDirect.hasSession();
      } finally {
        this.cloudagentLoginPromise = null;
      }
    })();
    return this.cloudagentLoginPromise;
  }

  /** Store usId/agentUniqId in OzonetelCloudagentDirectService when found in SDK/call payloads. */
  private async seedMdlSessionFromSdk(sdkResult?: unknown): Promise<boolean> {
    if (!this.config) {
      return false;
    }
    const { usId, agentUniqId } = await this.resolveMdlIdentity(sdkResult);
    if (!usId || agentUniqId == null || !Number.isFinite(agentUniqId)) {
      return false;
    }
    this.cloudagentDirect.setSession({
      customer: this.config.customer,
      agentId: this.config.agentId,
      usId,
      agentUniqId,
    });
    return true;
  }

  private async establishCloudagentMdlSession(
    config: OzonetelConfig,
    sdkResult: unknown,
    hybrid: boolean
  ): Promise<void> {
    if (!hybrid) {
      return;
    }
    this.config = await this.mergeConfigFromDb(config);
    this.lastSdkInitResult = sdkResult;
    this.cloudagentLoginBlocked = false;
    this.inboundToolbarPickBlocked = false;
    this.inboundToolbarPickAttempts = 0;
    const identity = await this.resolveMdlIdentity(sdkResult);
    if (identity.usId) {
      this.config.usId = identity.usId;
    }
    if (identity.agentUniqId != null && Number.isFinite(identity.agentUniqId)) {
      this.config.agentUniqId = identity.agentUniqId;
    }
    this.seedMdlSessionFromConfigHints();
    await this.seedMdlSessionFromSdk(sdkResult);
    if (this.cloudagentDirect.hasSession()) {
      const synced = await this.cloudagentDirect.syncSessionToServer();
      if (synced) {
        this.serverMdlSessionReady = true;
      }
    }
    this.agentConfigCache = null;
    const loginOk = await this.ensureCloudagentMdlSessionViaServer(false);
    if (!loginOk) {
      console.warn(
        '[OzonetelSDKCore] CloudAgent server MDL session not ready after SDK init — inbound toolbar pick may fail until login succeeds'
      );
    }
  }

  /**
   * Active-call fields for mdl payloads (must match iframeHttpsCurls).
   * Returns null if no ucid — hold/mute cannot run without an active call.
   */
  private async buildMdlSnapshot(): Promise<{
    ucid: string;
    monitorUcid: string;
    did: string;
    campignId: string;
    phoneForHold: string;
    agentPhoneForMute: string;
  } | null> {
    let cd: any = {};
    try {
      if (this.dc && typeof this.dc.getCallData === 'function') {
        cd = await this.dc.getCallData();
      }
    } catch {
    }
    const m = { ...(this.latestCallSignals || {}), ...(cd || {}) };
    const ucid = String(m.ucid || m.UCID || m.monitorUcid || m.monitorUCID || '').trim();
    if (!ucid) {
      return null;
    }
    const did = String(m.did || m.DID || '').trim();
    const monitorUcid = String(m.monitorUcid || m.monitorUCID || ucid).trim();
    const campignId = String(
      m.campignId ||
      m.campaignId ||
      m.campId ||
      m.CampaignId ||
      m.campaignID ||
      m.campaign_Id ||
      ''
    ).trim();
    const rawPhone =
      m.phoneNumber ||
      m.customerNumber ||
      m.callerId ||
      m.callerID ||
      m.customerPhone ||
      m.PhoneNumber ||
      m.agentNumber ||
      '';
    const phoneForHold = String(rawPhone).replace(/\D/g, '') || String(rawPhone).trim();
    const agentPhoneForMute = String(
      m.agentExtension ||
      m.extension ||
      m.Extension ||
      m.agentPhone ||
      m.phoneName ||
      this.config?.phoneNumber ||
      ''
    )
      .replace(/\D/g, '')
      .trim();
    return {
      ucid,
      monitorUcid: monitorUcid || ucid,
      did,
      campignId,
      phoneForHold,
      agentPhoneForMute:
        agentPhoneForMute ||
        (this.config?.phoneNumber ? String(this.config.phoneNumber).replace(/\D/g, '') : ''),
    };
  }

  /**
   * Hold/mute/unhold/unmute via CloudAgent MDL (server → mdlConnection.php).
   * Does not call /cloudagent/login; uses ensureMdlSessionForControls() only.
   */
  private async invokeCloudagentMdl(
    op: 'hold' | 'unhold' | 'mute' | 'unmute'
  ): Promise<{ used: boolean; success: boolean; message?: string }> {
    if (!useOzonetelCloudagentMdlForControls()) {
      return { used: false, success: false };
    }
    const snap = await this.buildMdlSnapshot();
    if (!snap) {
      return {
        used: true,
        success: false,
        message: 'No active call (ucid missing). Place or answer a call before hold/mute.',
      };
    }
    await this.ensureMdlSessionForControls();
    if (op === 'hold' && !snap.campignId) {
      return {
        used: true,
        success: false,
        message:
          'Hold needs campignId from the active call. Place a manual call with a campaign id or ensure getCallData exposes campaign id.',
      };
    }
    if ((op === 'mute' || op === 'unmute') && !snap.agentPhoneForMute) {
      return {
        used: true,
        success: false,
        message:
          'Mute needs agent phone / extension (phoneNumberToMute). Configure ozonetelDetails.phoneNumber or Extension on the agent profile.',
      };
    }
    let mdlType = '';
    let extra: Record<string, unknown> = {};
    if (op === 'hold') {
      mdlType = 'tbHold';
      extra = {
        phoneNumberToHold: snap.phoneForHold,
        campignId: snap.campignId,
        monitorUcid: snap.monitorUcid,
        ucid: snap.ucid,
        did: snap.did,
      };
    } else if (op === 'unhold') {
      mdlType = 'tbUnHold';
      extra = {
        phoneNumberToUnHold: snap.phoneForHold,
        monitorUcid: snap.monitorUcid,
        ucid: snap.ucid,
        did: snap.did,
      };
    } else if (op === 'mute') {
      mdlType = 'tbMute';
      extra = {
        phoneNumberToMute: snap.agentPhoneForMute,
        monitorUcid: snap.monitorUcid,
        ucid: snap.ucid,
        did: snap.did,
      };
    } else {
      mdlType = 'tbUnMute';
      extra = {
        phoneNumberToUnMute: snap.agentPhoneForMute,
        monitorUcid: snap.monitorUcid,
        ucid: snap.ucid,
        did: snap.did,
      };
    }
    if (!this.cloudagentDirect.hasSession()) {
      return {
        used: true,
        success: false,
        message:
          'CloudAgent mdl session missing (usId / agentUniqId). Reload after Ozonetel logout, or add usId and agentUniqId to ozonetelDetails in user profile.',
      };
    }
    const res = await this.cloudagentDirect.postMdl(mdlType, extra);
    if (res.ok) {
      return { used: true, success: true };
    }
    return {
      used: true,
      success: false,
      message: res.message || 'CloudAgent mdl reported failure',
    };
  }

  private async initializeOneCxi(userName: string): Promise<void> {
    if (!this.useSDK) {
      console.log('[OzonetelSDKCore] SDK disabled, skipping OneCxi initialization');
      return;
    }

    if (!userName) {
      console.warn('[OzonetelSDKCore] OneCxi userName missing, skipping');
      return;
    }

    if (typeof OneCxi === 'undefined') {
      console.warn('[OzonetelSDKCore] OneCxi SDK not loaded');
      return;
    }

    try {
      if (!ONE_CXI_API_KEY) {
        console.log('[OzonetelSDKCore] API key not yet available, fetching...');
        await this.fetchApiKey();

        if (!ONE_CXI_API_KEY) {
          console.error('[OzonetelSDKCore] Failed to fetch API key, cannot initialize OneCxi');
          return;
        }
      }

      if (!this.oneCXI) {
        this.oneCXI = new OneCxi();
        console.log('[OzonetelSDKCore] OneCxi instance created');
      }

      console.log('[OzonetelSDKCore] Initializing OneCxi for SIP registration', {
        userName: userName,
        apiKey: ONE_CXI_API_KEY ? 'present' : 'missing'
      });

      const oneCxiData = await this.oneCXI.initializeSDK(userName, ONE_CXI_API_KEY);
      console.log('[OzonetelSDKCore] OneCxi initializeSDK response:', oneCxiData);

      if (oneCxiData && oneCxiData.sip_url) {
        this.oneCxiInitialized = true;
        this.oneCxiDidNumbers = Array.isArray(oneCxiData.did_numbers) ? oneCxiData.did_numbers : [];
        this.oneCxiPreferredDid = this.oneCxiDidNumbers.length ? String(this.oneCxiDidNumbers[0]) : '';
        console.log('[OzonetelSDKCore] OneCxi SIP registration complete', {
          sipUrl: oneCxiData.sip_url,
          didNumbers: oneCxiData.did_numbers,
          extension: oneCxiData.extension
        });
      } else {
        console.warn('[OzonetelSDKCore] OneCxi initialization returned incomplete data', oneCxiData);
      }
    } catch (error) {
      console.error('[OzonetelSDKCore] OneCxi initialization error:', error);
    }
  }

  async setAgentMode(mode: string, status: string): Promise<any> {
    if (!this.useSDK) {
      console.log('[OzonetelSDKCore] Setting agent mode via backend API:', { mode, status });
      try {
        const result = await firstValueFrom(this.http.post(`${environment.api_url}/api/ozonetel/agent/mode`, { mode, status }));
        this.agentState.modeSet = true;
        this.agentState.status = status;
        this.agentState.mode = mode;
        this.emitAgentState();
        return result;
      } catch (error) {
        console.error('[OzonetelSDKCore] Backend set mode error:', error);
        throw error;
      }
    }

    if (!this.dc) {
      throw new Error('SDK not initialized');
    }

    console.log('[OzonetelSDKCore] Setting agent mode:', { mode, status });
    const result = await this.dc.setAgentMode(mode, status, false);
    console.log('[OzonetelSDKCore] Set mode result:', result);

    if (result && (result.resp === 'Success' || result.agentStatus === status)) {
      this.agentState.modeSet = true;
      this.agentState.status = status;
      this.agentState.mode = mode;
      this.emitAgentState();
    }

    return result;
  }

  async getCampaigns(): Promise<any> {
    if (!this.useSDK) {
      console.log('[OzonetelSDKCore] Fetching campaigns via backend API');
      try {
        const result = await firstValueFrom(this.http.get(`${environment.api_url}/api/ozonetel/campaigns`));
        return result;
      } catch (error) {
        console.error('[OzonetelSDKCore] Backend get campaigns error:', error);
        throw error;
      }
    }

    if (!this.dc) {
      throw new Error('SDK not initialized');
    }

    console.log('[OzonetelSDKCore] Fetching campaigns');
    const result = await this.dc.getCampaigns();
    console.log('[OzonetelSDKCore] Campaigns result:', result);
    return result;
  }

  async initManualCall(campaignId: string, phoneNumber: string, uui?: string): Promise<any> {
    if (!this.useSDK) {
      console.log('[OzonetelSDKCore] Initiating manual call via backend API:', { campaignId, phoneNumber, uui });
      try {
        const result = await firstValueFrom(this.http.post(`${environment.api_url}/api/ozonetel/call/manual`, {
          campId: campaignId,
          phoneNumber,
          uui
        }));
        return result;
      } catch (error) {
        console.error('[OzonetelSDKCore] Backend manual call error:', error);
        throw error;
      }
    }

    if (!this.dc) {
      throw new Error('SDK not initialized');
    }

    this.beginManualDialAttempt();
    console.log('[OzonetelSDKCore] Initiating manual call:', { campaignId, phoneNumber, uui });
    const result = await this.dc.initManualCall(campaignId, phoneNumber, false, uui || '');
    console.log('[OzonetelSDKCore] Manual call result:', result);
    if (this.isManualDialErrorResult(result)) {
      this.clearManualDialPending();
    }
    return result;
  }

  private isManualDialErrorResult(result: any): boolean {
    if (!result || typeof result !== 'object') {
      return true;
    }
    const status = String(result.status ?? result.Status ?? '').toLowerCase();
    if (['error', 'exception', 'failed', 'failure'].includes(status)) {
      return true;
    }
    const msg = String(result.message ?? result.resp ?? result.resultLong ?? '').toLowerCase();
    return (
      msg.includes('valid campaign') ||
      msg.includes('invalid campaign') ||
      msg.includes('invalid agent') ||
      msg.includes('not logged') ||
      (msg.includes('failed') && !msg.includes('queued'))
    );
  }

  /** Sets INBOUND/IDLE on Ozonetel and starts getCallData poll for inbound auto-answer. */
  async setAgentInboundReady(): Promise<boolean> {
    if (!this.dc) {
      return false;
    }
    try {
      await this.setAgentMode(OZONETEL_INBOUND_AGENT_MODE, 'IDLE');
      const result = await this.releaseAgent(OZONETEL_INBOUND_AGENT_MODE, 'IDLE');
      const ok =
        result?.success ||
        result?.agentStatus === 'IDLE' ||
        result?.resp === 'Success' ||
        result?.resp?.includes?.('Success');
      if (ok) {
        this.agentState.status = 'IDLE';
        this.agentState.mode = OZONETEL_INBOUND_AGENT_MODE;
        this.emitAgentState();
        this.startInboundCallPoll();
        this.startCallStateWatchdog();
      }
      return !!ok;
    } catch (e: any) {
      console.warn('[OzonetelSDKCore] setAgentInboundReady failed:', e?.message || e);
      return false;
    }
  }

  // --- Post-call cleanup and re-arm (prepare agent for the next inbound/outbound leg) ---

  /** Best-effort dc.hangupCall when disconnect was detected indirectly (watchdog), not via UI. */
  private async cleanupDcSipLegOnCallEnd(): Promise<void> {
    if (!this.dc || typeof this.dc.hangupCall !== 'function') {
      return;
    }
    try {
      const result = await this.dc.hangupCall();
      const msg = String(result?.message || result?.resp || result?.error || '').toLowerCase();
      const ok = result?.status === 'Success' || result?.resp === 'Success';
      if (!ok && !this.isCallAlreadyEndedMessage(msg)) {
        console.warn('[OzonetelSDKCore] DC hangup on call end:', result);
      }
    } catch (e: any) {
      const msg = String(e?.message || e).toLowerCase();
      if (!this.isCallAlreadyEndedMessage(msg)) {
        console.warn('[OzonetelSDKCore] DC hangup on call end error:', e?.message || e);
      }
    }
  }

  private async ensureSipHealthyAfterCallEnd(): Promise<void> {
    if (!this.dc || !this.isDigitalConsoleSipEnabled()) {
      return;
    }
    try {
      const connected =
        typeof this.dc.isSIPConnected !== 'function' || this.dc.isSIPConnected();
      console.log('[OzonetelSDKCore] Post-call SIP health:', { connected });
      if (connected) {
        return;
      }
      const userName = this.config?.userName || this.config?.customer;
      if (userName) {
        console.warn('[OzonetelSDKCore] SIP disconnected after call — re-initializing OneCxi');
        this.oneCxiInitialized = false;
        await this.initializeOneCxi(userName);
      }
    } catch (e) {
      console.warn('[OzonetelSDKCore] SIP health check failed:', e);
    }
  }

  private async retrySyncAgentReadyOnServer(mode: string, attempts: number): Promise<boolean> {
    const delays = [0, 500, 1000, 2000];
    for (let i = 0; i < attempts; i++) {
      if (i > 0) {
        await new Promise((r) => setTimeout(r, delays[i] ?? 500));
      }
      if (await this.syncAgentReadyOnServer(mode)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Central disconnect handler — all paths funnel here (hangup, remote disconnect, watchdog).
   *
   * DISPOSITION TRIGGER (step 1 of wrap-up — does NOT submit disposition here):
   * 1. captureWrapUpCallDataSnapshot — save leg data for later dc.setDisposition()
   * 2. wrapUpPending = true — agent cannot take another call until disposition succeeds
   * 3. emitCallEvent('DROP') — CallingComponent.enterOzonetelWrapUp() opens disposition UI
   * 4. deferRearmUntilWrapUp — skips syncAgentIdleAfterCallEnd until disposition completes
   */
  private handleCallEnded(data: any): void {
    const now = Date.now();
    if (now - this.lastCallEndAt < 500) {
      return;
    }
    this.lastCallEndAt = now;

    const endedUcid =
      this.extractUcid(data) ||
      this.extractUcid(this.latestCallSignals) ||
      this.activeCallUcid;
    if (endedUcid) {
      this.lastEndedUcid = endedUcid;
    }
    // Transfer-release bypass: Agent A leaves transferred call without entering wrap-up.
    const transferReleaseBypass =
      data?.transferReleaseBypass === true || this.consumeTransferReleaseWrapUpBypass(endedUcid);
    this.captureWrapUpCallDataSnapshot(data, endedUcid);
    this.postCallCooldownUntil = now + 12000;
    this.rearmInProgress = true;
    this.clearManualDialPending();

    this.activeCallUcid = '';
    this.callSessionActive = false;

    if (!this.shouldSkipLegCleanupOnEnd(data)) {
      this.pendingCallEndCleanup = true;
    }

    this.lastToolbarPickUcid = '';
    this.inboundToolbarPickBlocked = false;
    this.inboundToolbarPickAttempts = 0;
    this.inboundAutoAnswerUcids.clear();
    if (this.cloudagentDirect.hasSession()) {
      this.serverMdlSessionReady = true;
    }
    console.log('[OzonetelSDKCore] Handling call end/disconnect:', data);

    if (this.oneCXI && this.isStreaming) {
      try {
        this.oneCXI.stopStreaming();
        this.isStreaming = false;
        if (!this.isDigitalConsoleSipEnabled()) {
          this.oneCxiInitialized = false;
        }
      } catch (error) {
        console.error('[OzonetelSDKCore] Error stopping OneCxi stream:', error);
      }
    }

    const wasBusy = this.agentState.status === 'BUSY';
    this.latestCallSignals = null;

    const dropEventData = {
      ...data,
      message: 'Call ended',
      timestamp: Date.now(),
      source: data?.source || 'Unknown',
      event: data?.event || 'DROP',
      transferReleaseBypass
    };

    // Normal calls require wrap-up; transfer-release bypass skips disposition for Agent A.
    this.wrapUpPending = !transferReleaseBypass;
    this.emitCallEvent('DROP', dropEventData);

    this.agentState.status = 'IDLE';
    this.emitAgentState();
    console.log('[OzonetelSDKCore] Agent state updated to IDLE (was:', wasBusy ? 'BUSY' : this.agentState.status, ')');

    void this.deferRearmUntilWrapUp();
  }

  private armTransferReleaseWrapUpBypass(): void {
    // One-shot arm immediately before transfer-aware hangup.
    this.skipNextWrapUpForTransferRelease = true;
    this.skipWrapUpForTransferUntil = Date.now() + 15000;
    this.skipWrapUpForTransferUcid =
      this.activeCallUcid || this.extractUcid(this.latestCallSignals) || '';
  }

  private consumeTransferReleaseWrapUpBypass(endedUcid?: string): boolean {
    if (!this.skipNextWrapUpForTransferRelease) {
      return false;
    }
    if (Date.now() > this.skipWrapUpForTransferUntil) {
      this.skipNextWrapUpForTransferRelease = false;
      this.skipWrapUpForTransferUntil = 0;
      this.skipWrapUpForTransferUcid = '';
      return false;
    }
    if (
      this.skipWrapUpForTransferUcid &&
      endedUcid &&
      this.skipWrapUpForTransferUcid !== endedUcid
    ) {
      // Keep armed for the intended leg if a different disconnect event arrives first.
      return false;
    }
    this.skipNextWrapUpForTransferRelease = false;
    this.skipWrapUpForTransferUntil = 0;
    this.skipWrapUpForTransferUcid = '';
    return true;
  }

  /** True while agent must complete disposition before next inbound leg. */
  /** Exposed to CallingComponent / OzonetelWrapUpService — true while disposition is required. */
  isWrapUpPending(): boolean {
    return this.wrapUpPending;
  }

  // Clears stale wrap-up gate on session reuse when no active call exists.
  async clearStaleWrapUpStateIfNoActiveCall(): Promise<boolean> {
    if (!this.wrapUpPending || this.callSessionActive) {
      return false;
    }
    // Preserve real post-disconnect wrap-up; only clear stale state on aged/restored sessions.
    if (this.lastCallEndAt && Date.now() - this.lastCallEndAt < 30000) {
      return false;
    }
    this.wrapUpPending = false;
    this.wrapUpCallDataSnapshot = null;
    this.pendingCallEndCleanup = false;
    this.callEventSubject.next({ type: 'NONE' });
    await this.syncAgentIdleAfterCallEnd();
    return true;
  }

  /**
   * Step 4 of wrap-up: re-arm inbound only after disposition (see completeWrapUpAfterDisposition).
   * Called from handleCallEnded; returns early while wrapUpPending is true.
   */
  private async deferRearmUntilWrapUp(): Promise<void> {
    if (this.wrapUpPending) {
      console.log('[OzonetelSDKCore] Wrap-up pending — deferring inbound re-arm until disposition');
      return;
    }
    await this.syncAgentIdleAfterCallEnd();
  }

  /**
   * Step 6 of wrap-up: after successful dc.setDisposition — clear gate and re-arm agent.
   * Called only from submitWrapUpDisposition() on SDK success.
   */
  async completeWrapUpAfterDisposition(): Promise<void> {
    this.wrapUpPending = false;
    this.wrapUpCallDataSnapshot = null;
    if (this.pendingCallEndCleanup) {
      this.pendingCallEndCleanup = false;
      await this.cleanupDcSipLegOnCallEnd();
    }
    await this.syncAgentIdleAfterCallEnd();
  }

  /**
   * Step 1b of wrap-up: save call leg fields before SDK clears internal state.
   * dc.setDisposition reads monitorUcid/dataId from internal #D.getCallData() via setCallData snapshot.
   */
  private captureWrapUpCallDataSnapshot(endEventData: any, endedUcid: string): void {
    let cd: any = {};
    try {
      if (this.dc && typeof this.dc.getCallData === 'function') {
        const raw = this.dc.getCallData();
        if (raw && typeof raw === 'object' && typeof (raw as Promise<any>).then !== 'function') {
          cd = raw;
        }
      }
    } catch (e) {
      console.warn('[OzonetelSDKCore] getCallData during wrap-up snapshot failed:', e);
    }
    this.wrapUpCallDataSnapshot = this.buildWrapUpCallDataSnapshot(cd, endEventData, endedUcid);
    console.log('[OzonetelSDKCore] Wrap-up call data snapshot:', {
      monitorUcid: this.wrapUpCallDataSnapshot?.monitorUcid,
      ucid: this.wrapUpCallDataSnapshot?.ucid,
    });
  }

  /** Merges DC getCallData, latest signals, and disconnect event into one snapshot. */
  private buildWrapUpCallDataSnapshot(cd: any, endEventData: any, endedUcid: string): any {
    const merged = {
      ...(this.latestCallSignals || {}),
      ...(cd || {}),
      ...(endEventData || {}),
    };
    const monitorUcid = String(
      merged.agentMonitorUcid ||
        merged.monitorUcid ||
        merged.ucid ||
        merged.UCID ||
        endedUcid ||
        ''
    ).trim();
    return {
      ...merged,
      monitorUcid: monitorUcid || merged.monitorUcid,
      ucid: String(merged.ucid || merged.UCID || monitorUcid || endedUcid || '').trim(),
      did: merged.did || merged.DID || merged.callerID || merged.callerId || '',
      field: merged.field || merged.dataId || merged.campignId || merged.campaignId || '',
      dataId: merged.dataId || merged.campignId || merged.campaignId || merged.field || '',
      callerId: merged.callerId || merged.did || merged.DID || '',
      uui: merged.uui || merged.UUI || '',
      agentId: merged.agentId || this.config?.agentId || '',
      customer: merged.customer || merged.username || this.config?.customer || '',
      agentUniqId: merged.agentUniqId || this.config?.agentUniqId,
      mode: merged.mode || this.agentState?.mode || OZONETEL_INBOUND_AGENT_MODE,
    };
  }

  private resolveWrapUpAgentModeFallback(): string {
    const mode =
      this.wrapUpCallDataSnapshot?.mode ||
      this.agentState?.mode ||
      OZONETEL_INBOUND_AGENT_MODE;
    return String(mode || OZONETEL_INBOUND_AGENT_MODE).trim() || OZONETEL_INBOUND_AGENT_MODE;
  }

  /**
   * Step 5b: restore call snapshot + patch getAgentMode before dc disposition APIs.
   * SDK reads internal #D.getCallData() — not our patched dc.getCallData() alone.
   */
  private async withWrapUpDispositionContext<T>(operation: () => Promise<T>): Promise<T> {
    if (!this.dc) {
      return operation();
    }
    const restoreAgentMode = this.patchDcGetAgentModeForDisposition();
    const snapshot = this.wrapUpCallDataSnapshot;
    if (snapshot && typeof this.dc.setCallData === 'function') {
      try {
        this.dc.setCallData({ ...snapshot });
      } catch (e) {
        console.warn('[OzonetelSDKCore] setCallData during wrap-up failed:', e);
      }
    }
    try {
      return await operation();
    } finally {
      restoreAgentMode();
    }
  }

  /** Patches dc.getAgentMode() so SDK getDispositions() does not call null.toLowerCase(). */
  private patchDcGetAgentModeForDisposition(): () => void {
    if (!this.dc) {
      return () => {};
    }
    const mode = this.resolveWrapUpAgentModeFallback();
    const safeGetter = () => mode;
    const restores: Array<() => void> = [];
    const patch = (target: any, key: string) => {
      if (!target || typeof target[key] !== 'function') {
        return;
      }
      const original = target[key];
      target[key] = safeGetter;
      restores.push(() => {
        target[key] = original;
      });
    };
    patch(this.dc, 'getAgentMode');
    const proto = Object.getPrototypeOf(this.dc);
    if (proto) {
      Object.getOwnPropertyNames(proto).forEach((key) => {
        if (/getAgentMode/i.test(key)) {
          patch(proto, key);
        }
      });
    }
    return () => restores.forEach((restore) => restore());
  }

  /** Step 5 of wrap-up: send disposition to Ozonetel; completes wrap-up only on SDK success. */
  async submitWrapUpDisposition(disposition: string, comment?: string): Promise<{ success: boolean; message?: string; result?: any }> {
    const result = await this.setDisposition(disposition, comment);
    const resp = String(result?.resp ?? result?.status ?? '').toLowerCase();
    const ok = resp === 'success' || result?.success === true;
    if (!ok) {
      const message = result?.message || result?.resp || 'Failed to set disposition';
      return { success: false, message, result };
    }
    await this.completeWrapUpAfterDisposition();
    return { success: true, result };
  }

  private async syncAgentReadyOnServer(mode: string = OZONETEL_MANUAL_DIAL_MODE): Promise<boolean> {
    try {
      const result: any = await firstValueFrom(
        this.http.post(`${environment.api_url}/api/ozonetel/agent/ready`, { mode })
      );
      const ok = result?.resultShort === 'success' || result?.resultShort === 'Success';
      console.log('[OzonetelSDKCore] Server changeAgentState Ready:', result);
      return ok;
    } catch (e) {
      console.warn('[OzonetelSDKCore] Server agent ready sync failed:', e);
      return false;
    }
  }

  /**
   * Prepare SDK before phone.component / backend manual-dial API.
   * Sets MANUAL/IDLE, waits for SIP, and opens manualDialPending grace for watchdog.
   */
  async ensureAgentReadyForManualDial(): Promise<{ ready: boolean; message?: string }> {
    if (this.wrapUpPending) {
      return { ready: false, message: 'Complete call disposition first' };
    }
    await this.waitForRearmComplete(5000);
    this.beginManualDialAttempt();
    if (this.dc) {
      try {
        // SDK path only — dc.setAgentMode/releaseAgent is enough; server changeAgentState often 400s here.
        await this.setAgentMode(OZONETEL_MANUAL_DIAL_MODE, 'IDLE');
        const result = await this.releaseAgent(OZONETEL_MANUAL_DIAL_MODE, 'IDLE');
        const ok =
          result?.success ||
          result?.agentStatus === 'IDLE' ||
          result?.resp === 'Success' ||
          result?.resp?.includes?.('Success');
        if (ok) {
          this.agentState.status = 'IDLE';
          this.agentState.mode = OZONETEL_MANUAL_DIAL_MODE;
          this.emitAgentState();
        }
      } catch (e: any) {
        console.warn('[OzonetelSDKCore] SDK idle sync before dial:', e?.message || e);
      }
      // Manual dial on DC SIP rings the agent leg — wait for SIP registration before customer is bridged.
      if (
        this.isDigitalConsoleSipEnabled() &&
        typeof this.dc.isSIPConnected === 'function'
      ) {
        for (let i = 0; i < 25 && !this.dc.isSIPConnected(); i++) {
          await new Promise((r) => setTimeout(r, 100));
        }
      }
    }
    await new Promise((r) => setTimeout(r, 300));
    return { ready: true };
  }

  /**
   * After disconnect: sync Ozonetel server Ready (changeAgentState), refresh MDL if needed,
   * then dc.releaseAgent INBOUND/IDLE so the next call can be routed to this agent.
   */
  private async syncAgentIdleAfterCallEnd(): Promise<void> {
    try {
      await new Promise((r) => setTimeout(r, 500));
      if (!this.dc) {
        this.agentState.status = 'IDLE';
        this.emitAgentState();
        return;
      }

      const serverOk = await this.retrySyncAgentReadyOnServer(OZONETEL_INBOUND_AGENT_MODE, 3);
      if (!serverOk) {
        console.warn('[OzonetelSDKCore] Post-call server Ready sync failed — agent may not receive next call');
      }

      if (!this.serverMdlSessionReady) {
        await this.ensureCloudagentMdlSessionViaServer(true);
      }

      const ready = await this.setAgentInboundReady();
      if (!ready) {
        console.warn('[OzonetelSDKCore] Re-arm INBOUND after call end failed — retrying once');
        await new Promise((r) => setTimeout(r, 800));
        await this.retrySyncAgentReadyOnServer(OZONETEL_INBOUND_AGENT_MODE, 2);
        await this.setAgentInboundReady();
      }

      await this.ensureSipHealthyAfterCallEnd();
    } finally {
      this.rearmInProgress = false;
    }
  }

  private emitInboundScreenPopForCaseLookup(callData: any, custData: unknown): void {
    const ucid = this.extractUcid(callData);
    if (!this.canPromoteCallToActive(ucid)) {
      return;
    }
    const merged = {
      ...(callData || {}),
      ...(this.latestCallSignals || {}),
      custData,
    };
    const enriched = this.dialerFlow.enrichCallDisplayData(merged);
    this.latestCallSignals = enriched;
    this.markActiveCall(enriched);
    this.agentState.status = 'BUSY';
    this.emitAgentState();
    this.emitCallEvent('INBOUND_SCREEN_POP', enriched);
    this.emitCallEvent('BUSY', enriched);
  }

  async hangupCall(options?: { skipWrapUpForTransferRelease?: boolean }): Promise<any> {
    // Used by transfer-release flow so Agent A exits without wrap-up.
    const transferReleaseBypass = options?.skipWrapUpForTransferRelease === true;
    if (transferReleaseBypass) {
      this.armTransferReleaseWrapUpBypass();
    }
    // For SIP/WebRTC calls, use OneCxi to stop streaming
    if (this.oneCXI && this.isStreaming) {
      console.log('[OzonetelSDKCore] Hanging up SIP call via OneCxi');
      try {
        this.oneCXI.stopStreaming();
        this.isStreaming = false;
        this.oneCxiInitialized = false;

        // Also call DigitalConsole hangup to update server state
        if (this.dc) {
          await this.dc.hangupCall();
        }

        this.handleCallEnded({
          status: 'Success',
          message: 'Call ended via OneCxi',
          source: 'OneCxi',
          transferReleaseBypass
        });
        return { status: 'Success', message: 'Call ended via OneCxi', success: true };
      } catch (e: any) {
        const msg = String(e?.message || e).toLowerCase();
        if (this.isCallAlreadyEndedMessage(msg)) {
          this.handleCallEnded({
            status: 'Success',
            message: e.message,
            source: 'OneCxi',
            alreadyEnded: true,
            transferReleaseBypass
          });
          return { status: 'Success', message: e.message, success: true, alreadyEnded: true };
        }
        console.error('[OzonetelSDKCore] OneCxi hangup error:', e);
        return { status: 'Error', message: e.message, success: false };
      }
    }

    // For telephony calls, use DigitalConsole
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Hanging up telephony call via DigitalConsole');
    const result = await this.dc.hangupCall();
    console.log('[OzonetelSDKCore] Hangup result:', result);

    const isSuccess = result?.status === 'Success' || result?.resp === 'Success';
    const msg = String(result?.message || result?.resp || result?.error || '').toLowerCase();
    const alreadyEnded = !isSuccess && this.isCallAlreadyEndedMessage(msg);
    if (isSuccess || alreadyEnded) {
      this.handleCallEnded({ ...result, source: 'DigitalConsole', alreadyEnded, transferReleaseBypass });
    }
    return { ...result, success: isSuccess || alreadyEnded, alreadyEnded };
  }

  /** Prefer CloudAgent MDL hold; fallback OneCxi toggleHold or dc.holdCall. */
  async holdCall(): Promise<any> {
    const mdl = await this.invokeCloudagentMdl('hold');
    if (mdl.used) {
      return mdl.success
        ? { status: 'Success', resp: 'Success', success: true, source: 'cloudagent_mdl' }
        : { status: 'Error', message: mdl.message, success: false, source: 'cloudagent_mdl' };
    }

    if (this.oneCXI && this.isStreaming) {
      console.log('[OzonetelSDKCore] Holding call via OneCxi');
      try {
        this.oneCXI.toggleHold();
        return { status: 'Success', message: 'Call on hold', success: true };
      } catch (e: any) {
        console.error('[OzonetelSDKCore] OneCxi hold error:', e);
        return { status: 'Error', message: e.message, success: false };
      }
    }

    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Holding call via DigitalConsole');
    const result = await this.dc.holdCall();
    console.log('[OzonetelSDKCore] Hold result:', result);
    const isSuccess = result?.status === 'Success' || result?.resp === 'Success';
    return { ...result, success: isSuccess };
  }

  /** Prefer CloudAgent MDL unhold; fallback OneCxi or dc.unHoldCall. */
  async unHoldCall(): Promise<any> {
    const mdl = await this.invokeCloudagentMdl('unhold');
    if (mdl.used) {
      return mdl.success
        ? { status: 'Success', resp: 'Success', success: true, source: 'cloudagent_mdl' }
        : { status: 'Error', message: mdl.message, success: false, source: 'cloudagent_mdl' };
    }

    if (this.oneCXI && this.isStreaming) {
      console.log('[OzonetelSDKCore] Resuming call via OneCxi');
      try {
        this.oneCXI.toggleHold();
        return { status: 'Success', message: 'Call resumed', success: true };
      } catch (e: any) {
        console.error('[OzonetelSDKCore] OneCxi unhold error:', e);
        return { status: 'Error', message: e.message, success: false };
      }
    }

    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Unholding call via DigitalConsole');
    const result = await this.dc.unHoldCall();
    console.log('[OzonetelSDKCore] Unhold result:', result);
    const isSuccess = result?.status === 'Success' || result?.resp === 'Success';
    return { ...result, success: isSuccess };
  }

  /** Prefer CloudAgent MDL mute; fallback OneCxi or dc.muteCall. */
  async muteCall(): Promise<any> {
    const mdl = await this.invokeCloudagentMdl('mute');
    if (mdl.used) {
      return mdl.success
        ? { status: 'Success', resp: 'Success', success: true, source: 'cloudagent_mdl' }
        : { status: 'Error', message: mdl.message, success: false, source: 'cloudagent_mdl' };
    }

    if (this.oneCXI && this.isStreaming) {
      console.log('[OzonetelSDKCore] Muting call via OneCxi');
      try {
        this.oneCXI.toggleMute();
        return { status: 'Success', message: 'Call muted', success: true };
      } catch (e: any) {
        console.error('[OzonetelSDKCore] OneCxi mute error:', e);
        return { status: 'Error', message: e.message, success: false };
      }
    }

    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Muting call via DigitalConsole');
    const result = await this.dc.muteCall();
    console.log('[OzonetelSDKCore] Mute result:', result);
    const isSuccess = result?.status === 'Success' || result?.resp === 'Success';
    return { ...result, success: isSuccess };
  }

  /** Prefer CloudAgent MDL unmute; fallback OneCxi or dc.unMuteCall. */
  async unMuteCall(): Promise<any> {
    const mdl = await this.invokeCloudagentMdl('unmute');
    if (mdl.used) {
      return mdl.success
        ? { status: 'Success', resp: 'Success', success: true, source: 'cloudagent_mdl' }
        : { status: 'Error', message: mdl.message, success: false, source: 'cloudagent_mdl' };
    }

    if (this.oneCXI && this.isStreaming) {
      console.log('[OzonetelSDKCore] Unmuting call via OneCxi');
      try {
        this.oneCXI.toggleMute();
        return { status: 'Success', message: 'Call unmuted', success: true };
      } catch (e: any) {
        console.error('[OzonetelSDKCore] OneCxi unmute error:', e);
        return { status: 'Error', message: e.message, success: false };
      }
    }

    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Unmuting call via DigitalConsole');
    const result = await this.dc.unMuteCall();
    console.log('[OzonetelSDKCore] Unmute result:', result);
    const isSuccess = result?.status === 'Success' || result?.resp === 'Success';
    return { ...result, success: isSuccess };
  }

  // Agent State Methods
  async setPauseState(pauseReason: string, mode: string = OZONETEL_INBOUND_AGENT_MODE): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Setting pause state:', { pauseReason, mode });
    const result = await this.dc.setPauseState(pauseReason, mode);
    console.log('[OzonetelSDKCore] Pause state result:', result);

    // DO NOT check type === 'response' - it's true for both success and error!
    const isSuccess = result?.status === 'Success' || result?.resp === 'Success' ||
      result?.agentStatus === 'PAUSED' || result?.resp?.includes?.('Success');
    if (isSuccess) {
      this.agentState.status = 'PAUSED';
      this.emitAgentState();
    }
    return { ...result, success: isSuccess };
  }

  async releaseAgent(mode: string = OZONETEL_INBOUND_AGENT_MODE, campaignId?: string, message?: string): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Releasing agent:', { mode, campaignId, message });
    const result = await this.dc.releaseAgent(mode, campaignId || '', message || '');
    console.log('[OzonetelSDKCore] Release agent result:', result);

    const isSuccess = result?.agentStatus === 'IDLE' || result?.resp === 'Success' ||
      result?.resp?.includes?.('Success');
    if (isSuccess) {
      this.agentState.status = 'IDLE';
      this.emitAgentState();
    }
    return { ...result, success: isSuccess };
  }

  // Call Data Methods
  async getCallData(): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Getting call data');
    const result = await this.dc.getCallData();
    console.log('[OzonetelSDKCore] Call data:', result);
    return result;
  }

  // --- Disposition (wrap-up / ACW) — see WRAP-UP / DISPOSITION FLOW in file header ---

  /**
   * Raw Ozonetel SDK — dc.getDispositions().
   * Not used for UI list (dialersDispositionList.json S3); kept for optional/debug use.
   */
  async getDispositions(): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    return this.withWrapUpDispositionContext(() => this.dc.getDispositions());
  }

  /**
   * Raw Ozonetel SDK — dc.setDisposition(disposition, comment, callbackTz, useCallDataDisposition, extra).
   * 4th arg MUST be false: when true, SDK ignores disposition param and Ozonetel records "Wrap up time exceeded".
   */
  async setDisposition(disposition: string, comment?: string): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    return this.withWrapUpDispositionContext(() =>
      this.dc.setDisposition(disposition, comment || '', '', false, '')
    );
  }

  // DTMF
  async sendDTMF(digit: string): Promise<any> {
    // For SIP/WebRTC calls, use OneCxi
    if (this.oneCXI && this.isStreaming) {
      console.log('[OzonetelSDKCore] Sending DTMF via OneCxi:', digit);
      try {
        this.oneCXI.sendDTMF(digit);
        return { status: 'Success', message: 'DTMF sent' };
      } catch (e: any) {
        console.error('[OzonetelSDKCore] OneCxi DTMF error:', e);
        return { status: 'Error', message: e.message };
      }
    }

    // For telephony calls, use DigitalConsole
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Sending DTMF via DigitalConsole:', digit);
    const result = await this.dc.sendDTMF(digit);
    return result;
  }

  // Transfer Methods
  async getTransferList(transferType: number): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    // transferType: 1=Agent, 2=Skill, 3=Phone, 4=IVR
    console.log('[OzonetelSDKCore] Getting transfer list:', transferType);
    const result = await this.dc.getTransferList(transferType);
    console.log('[OzonetelSDKCore] Transfer list:', result);
    return result;
  }

  async initTransferCall(modeOfTransfer: string, warmOrBlind: number, transferList: any, transferTo: string, flowId?: string): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    // warmOrBlind: 1=Blind, 3=Warm
    console.log('[OzonetelSDKCore] Initiating transfer:', { modeOfTransfer, warmOrBlind, transferTo });
    const result = await this.dc.initTransferCall(modeOfTransfer, warmOrBlind, transferList, transferTo, flowId || '');
    console.log('[OzonetelSDKCore] Transfer result:', result);
    return result;
  }

  // Conference Methods
  async getConferenceList(confType: number): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    // confType: 1=Agent, 3=Phone
    console.log('[OzonetelSDKCore] Getting conference list:', confType);
    const result = await this.dc.getConferenceList(confType);
    console.log('[OzonetelSDKCore] Conference list:', result);
    return result;
  }

  async initConferenceCall(confType: number, participant: string): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Initiating conference:', { confType, participant });
    const result = await this.dc.initConferenceCall(confType, participant);
    console.log('[OzonetelSDKCore] Conference result:', result);
    return result;
  }

  async kickAgentFromCall(): Promise<any> {
    if (!this.dc) {
      throw new Error('SDK not initialized');
    }
    console.log('[OzonetelSDKCore] Kicking agent from conference');
    const result = await this.dc.kickAgentFromCall();
    console.log('[OzonetelSDKCore] Kick result:', result);
    return result;
  }

  // Utility Methods
  isAgentInitialized(): boolean {
    return this.agentState.initialized && this.agentState.loggedIn;
  }

  getAgentState(): AgentState {
    return { ...this.agentState };
  }

  getCurrentCallState(): { isStreaming: boolean; oneCxiInitialized: boolean } {
    return {
      isStreaming: this.isStreaming,
      oneCxiInitialized: this.oneCxiInitialized
    };
  }

  /**
   * Inbound vs outbound heuristic for onNewCall / auto-answer.
   * When agent mode is INBOUND, treat ringing legs as inbound unless callType is manual/outbound/etc.
   */
  private isInboundSdkCall(data: any): boolean {
    if (this.agentState.mode === OZONETEL_INBOUND_AGENT_MODE) {
      if (!data) {
        return true;
      }
      const type = String(data.callType ?? data.calltype ?? data.type ?? '').toLowerCase();
      if (
        type.includes('manual') ||
        type.includes('outbound') ||
        type.includes('preview') ||
        type.includes('progressive') ||
        type.includes('predictive')
      ) {
        return false;
      }
      if (type.includes('transfer')) {
        return true;
      }
      return true;
    }
    if (!data) {
      return false;
    }
    const type = String(data.callType ?? data.calltype ?? data.type ?? '').toLowerCase();
    if (
      type.includes('manual') ||
      type.includes('outbound') ||
      type.includes('preview') ||
      type.includes('progressive') ||
      type.includes('predictive')
    ) {
      return false;
    }
    if (type.includes('inbound') || type.includes('incoming') || type.includes('transfer')) {
      return true;
    }
    const dir = String(data.direction ?? data.callDirection ?? '').toLowerCase();
    return dir.includes('inbound') || dir === 'in' || dir === 'incoming';
  }

  /** Manual / progressive / preview legs — not inbound. Audio path differs from inbound toolbar pick. */
  private isOutboundSdkLeg(data: any): boolean {
    if (this.isManualDialPending() || this.agentState.mode === OZONETEL_MANUAL_DIAL_MODE) {
      if (!data || !this.isInboundSdkCall(data)) {
        return true;
      }
    }
    if (!data) {
      return false;
    }
    const type = String(
      data.callType ?? data.calltype ?? data.type ?? data.mode ?? ''
    ).toLowerCase();
    return (
      type.includes('manual') ||
      type.includes('outbound') ||
      type.includes('preview') ||
      type.includes('progressive') ||
      type.includes('predictive')
    );
  }

  /** True when dc.initialize used sip:true and DC reports SIP/WebRTC enabled. */
  private isDigitalConsoleSipEnabled(): boolean {
    if (this.config?.sip === false) {
      return false;
    }
    try {
      return typeof this.dc?.isSIPEnabled === 'function' && this.dc.isSIPEnabled();
    } catch {
      return true;
    }
  }

  /**
   * OneCxi.startStreaming() opens an outbound WebSocket (call: "outbound").
   * When DC SIP is on, all legs (inbound + manual) use dc.sipAccept() — never OneCxi here.
   */
  private shouldStartOneCxiOutboundStream(callData: any): boolean {
    if (this.isDigitalConsoleSipEnabled()) {
      return false;
    }
    if (!this.isInboundSdkCall(callData)) {
      return true;
    }
    if (!useDigitalConsoleSipForInboundAudio()) {
      return true;
    }
    return false;
  }

  /**
   * Query fields for toolbar/getCustomerInfo — must match iframe Network tab:
   * params (caller digits), callType "false", monitorUcid from SDK ucid.
   */
  private toolbarFieldsFromCall(callData: any): Record<string, unknown> | null {
    const m = { ...(this.latestCallSignals || {}), ...(callData || {}) };
    const ucid = String(m.ucid || m.monitorUcid || m.monitorUCID || '').trim();
    const raw =
      m.callerId ||
      m.customerNumber ||
      m.phoneNumber ||
      m.params ||
      '';
    const params = String(raw).replace(/\D/g, '') || String(raw).trim();
    if (!params && !ucid) {
      return null;
    }
    return {
      params,
      callType: 'false',
      monitorUcid: ucid,
      ucid,
    };
  }

  private resetInboundPickGuardsForUcid(ucid: string): void {
    if (!ucid) {
      return;
    }
    if (ucid !== this.lastToolbarPickUcid) {
      this.inboundToolbarPickAttempts = 0;
      this.inboundToolbarPickBlocked = false;
    }
  }

  private async pickInboundViaToolbar(callData: any): Promise<boolean> {
    const fields = this.toolbarFieldsFromCall(callData);
    if (!fields) {
      return false;
    }
    const ucid = String(fields['monitorUcid'] || fields['ucid'] || '');
    this.resetInboundPickGuardsForUcid(ucid);
    if (this.inboundToolbarPickBlocked) {
      return false;
    }
    if (ucid && ucid === this.lastToolbarPickUcid) {
      if (
        this.canPromoteCallToActive(ucid) &&
        this.agentState.status === 'IDLE' &&
        !this.isStreaming &&
        this.isDigitalConsoleSipEnabled() &&
        useDigitalConsoleSipForInboundAudio()
      ) {
        this.trySipAcceptOnce(callData);
      }
      return true;
    }
    if (!(await this.ensureMdlSessionForInboundPick())) {
      console.warn('[OzonetelSDKCore] toolbar pick skipped — server MDL session not ready');
      return false;
    }
    const identity =
      this.config?.customer && this.config?.agentId
        ? { customer: this.config.customer, agentId: this.config.agentId }
        : undefined;
    const res = await this.cloudagentDirect.toolbarPickInbound(fields, identity);
    if (res.ok) {
      if (ucid) {
        this.lastToolbarPickUcid = ucid;
      }
      this.inboundToolbarPickAttempts = 0;
      this.inboundToolbarPickBlocked = false;
      const toolbarBody = res.data as { custData?: unknown };
      if (toolbarBody?.custData) {
        this.emitInboundScreenPopForCaseLookup(callData, toolbarBody.custData);
      }
      console.log(
        '[OzonetelSDKCore] toolbar getCustomerInfo OK (screen-pop) — running sipAccept for audio',
        fields
      );
      return true;
    }
    this.inboundToolbarPickAttempts += 1;
    if (this.inboundToolbarPickAttempts >= 5) {
      this.inboundToolbarPickBlocked = true;
    }
    if (res.message) {
      console.warn('[OzonetelSDKCore] toolbar pick:', res.message);
    }
    return false;
  }

  /** Entry point from DC events — delegates to autoAnswerInboundCall (toolbar + sipAccept). */
  private scheduleInboundToolbarPick(source: string, callData?: any): void {
    if (!shouldAutoAnswerOzonetelInbound()) {
      return;
    }
    if (this.isOutboundSdkLeg(callData || this.latestCallSignals)) {
      return;
    }
    if (callData) {
      this.latestCallSignals = { ...(this.latestCallSignals || {}), ...(callData || {}) };
    }
    const ucid =
      this.extractUcid(callData) || this.extractUcid(this.latestCallSignals);
    if (!this.canPromoteCallToActive(ucid)) {
      return;
    }
    this.resetInboundPickGuardsForUcid(ucid);
    console.log(`[OzonetelSDKCore] scheduleInboundToolbarPick (${source})`);
    void this.autoAnswerInboundCall(callData || this.latestCallSignals);
  }

  /**
   * Manual dial auto-answer for DC SIP agents.
   * Ozonetel rings the agent SIP leg on newCall; without sipAccept() within ~1s → dropCall NoAnswer.
   */
  private async autoAnswerOutboundSipCall(callData: any): Promise<void> {
    if (!this.dc || !this.isDigitalConsoleSipEnabled() || !this.isOutboundSdkLeg(callData)) {
      return;
    }
    const answerUcid = this.extractUcid(callData) || '_pending';
    if (answerUcid !== '_pending' && !this.canPromoteCallToActive(answerUcid)) {
      return;
    }
    if (this.inboundAutoAnswerUcids.has(answerUcid)) {
      return;
    }
    this.inboundAutoAnswerUcids.add(answerUcid);
    try {
      const delays = [0, 30, 60, 120, 250, 500, 1000, 2000];
      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) {
          await new Promise((r) => setTimeout(r, delays[i] - delays[i - 1]));
        }
        if (this.trySipAcceptOnce(callData)) {
          this.clearManualDialPending();
          this.scheduleInboundConnectUnhold(callData, 'manual-sip');
          return;
        }
      }
      console.warn('[OzonetelSDKCore] Manual/outbound sipAccept did not connect in time');
    } finally {
      this.inboundAutoAnswerUcids.delete(answerUcid);
    }
  }

  // --- Inbound poll + call-state watchdog (safety nets when DC WS events are missed) ---

  /**
   * Polls getCallData while INBOUND/IDLE to trigger auto-answer when ucid appears
   * before onNewCall fires. Does not emit NEW_CALL — watchdog handles UI sync.
   */
  private startInboundCallPoll(): void {
    if (this.inboundCallPollTimer || !this.dc) {
      return;
    }
    this.inboundCallPollTimer = setInterval(() => {
      if (
        !this.dc ||
        this.agentState.mode !== OZONETEL_INBOUND_AGENT_MODE ||
        this.inboundToolbarPickBlocked
      ) {
        return;
      }
      const status = this.agentState.status;
      const activeUcid = String(
        this.latestCallSignals?.ucid || this.latestCallSignals?.monitorUcid || ''
      ).trim();
      const canPoll =
        status === 'IDLE' ||
        status === 'NOT_READY' ||
        (status === 'BUSY' && !activeUcid);
      if (!canPoll) {
        return;
      }
      void (async () => {
        try {
          if (typeof this.dc.getCallData !== 'function') {
            return;
          }
          const cd = await this.dc.getCallData();
          if (!this.isCallDataActive(cd)) {
            return;
          }
          const ucid = this.extractUcid(cd);
          if (!this.canPromoteCallToActive(ucid)) {
            return;
          }
          if (status === 'BUSY' && !activeUcid) {
            this.latestCallSignals = { ...(this.latestCallSignals || {}), ...cd };
          }
          this.scheduleInboundToolbarPick('getCallData-poll', cd);
        } catch {
        }
      })();
    }, 750);
  }

  private stopInboundCallPoll(): void {
    if (this.inboundCallPollTimer) {
      clearInterval(this.inboundCallPollTimer);
      this.inboundCallPollTimer = null;
    }
  }

  /** Started on setAgentInboundReady — runs for the whole logged-in session. */
  private startCallStateWatchdog(): void {
    if (this.callStateWatchdogTimer || !this.dc) {
      return;
    }
    this.callStateWatchdogTimer = setInterval(() => {
      if (!this.dc || !this.agentState.loggedIn || this.watchdogHandling) {
        return;
      }
      void this.syncCallStateFromWatchdog();
    }, 500);
  }

  private stopCallStateWatchdog(): void {
    if (this.callStateWatchdogTimer) {
      clearInterval(this.callStateWatchdogTimer);
      this.callStateWatchdogTimer = null;
    }
  }

  /**
   * Watchdog responsibilities:
   * 1) Remote hangup: getCallData throws/empty or isRemoteCallEnded → handleCallEnded.
   * 2) Missed UI sync: audio connected (BUSY) but callSessionActive false → emit NEW_CALL/BUSY.
   * During manualDialPending, empty getCallData is normal while the leg is still connecting.
   */
  private async syncCallStateFromWatchdog(): Promise<void> {
    if (!this.dc || typeof this.dc.getCallData !== 'function' || this.rearmInProgress) {
      return;
    }
    if (!this.callSessionActive && this.agentState.status !== 'BUSY' && !this.isManualDialPending()) {
      return;
    }
    this.watchdogHandling = true;
    try {
      let cd: any = null;
      try {
        cd = await this.dc.getCallData();
      } catch {
        if (
          !this.isInCallConnectGrace() &&
          !this.isManualDialPending() &&
          (this.callSessionActive || this.agentState.status === 'BUSY')
        ) {
          console.log('[OzonetelSDKCore] Watchdog: getCallData empty — remote hangup');
          this.handleCallEnded({ source: 'watchdog', event: 'getCallDataEmpty' });
        }
        return;
      }

      if (this.isRemoteCallEnded(cd)) {
        console.log('[OzonetelSDKCore] Watchdog: remote hangup detected');
        this.handleCallEnded({
          source: 'watchdog',
          event: 'remoteHangup',
          ucid: this.extractUcid(cd),
          endTime: cd?.endTime,
        });
        return;
      }

      if (!cd || typeof cd !== 'object') {
        if (
          !this.isInCallConnectGrace() &&
          !this.isManualDialPending() &&
          (this.callSessionActive || this.agentState.status === 'BUSY')
        ) {
          this.handleCallEnded({ source: 'watchdog', event: 'noCallData' });
        }
        return;
      }

      const ucid = this.extractUcid(cd);
      const active = this.isCallDataActive(cd);

      if (active && this.canPromoteCallToActive(ucid)) {
        const localIdle =
          this.agentState.status === 'IDLE' || this.agentState.status === 'NOT_READY';
        // Audio may connect via sipAccept while widget still shows Idle/Disconnected
        const needsUiSync =
          localIdle || (!this.callSessionActive && this.agentState.status === 'BUSY');
        if (needsUiSync) {
          const trackedUcid =
            this.activeCallUcid || this.extractUcid(this.latestCallSignals) || '';
          const alreadySynced =
            !!ucid &&
            this.callSessionActive &&
            trackedUcid === ucid &&
            !!this.dialerFlow.resolveFullName(this.latestCallSignals);
          if (alreadySynced) {
            if (this.agentState.status !== 'BUSY') {
              this.agentState.status = 'BUSY';
              this.emitAgentState();
            }
            return;
          }
          console.log('[OzonetelSDKCore] Watchdog: active call detected, syncing UI', { ucid });
          this.clearEndedCallGuardsForNewLeg(ucid);
          this.latestCallSignals = { ...(this.latestCallSignals || {}), ...cd };
          this.markActiveCall(cd);
          this.agentState.status = 'BUSY';
          this.emitAgentState();
          this.emitCallEvent('NEW_CALL', cd);
          this.emitCallEvent('BUSY', cd);
          this.inboundToolbarPickBlocked = false;
          this.inboundToolbarPickAttempts = 0;
          if (shouldAutoAnswerOzonetelInbound() && this.isInboundSdkCall(cd)) {
            void this.autoAnswerInboundCall(cd);
          }
        }
        return;
      }

      if (
        !this.isInCallConnectGrace() &&
        !this.isInPostCallCooldown() &&
        (this.callSessionActive || this.agentState.status === 'BUSY') &&
        (cd.endTime || ucid === this.lastEndedUcid || this.isRemoteCallEnded(cd))
      ) {
        console.log('[OzonetelSDKCore] Watchdog: call ended, syncing disconnect');
        this.handleCallEnded({
          source: 'watchdog',
          event: 'callEnded',
          ucid,
          endTime: cd.endTime,
        });
      }
    } catch (e) {
      console.warn('[OzonetelSDKCore] Watchdog error:', e);
    } finally {
      this.watchdogHandling = false;
    }
  }

  /**
   * Step 2: connect inbound audio. DigitalConsole exposes sipAccept(), not answerSIPCall().
   * See dc-sdk.cloudagent.ozonetel.com/digitalConsole.js handleSIPAutoAnswer → sipAccept.
   */
  private trySipAcceptOnce(callData: any): boolean {
    if (!this.dc || !this.isDigitalConsoleSipEnabled()) {
      return false;
    }
    try {
      if (typeof this.dc.sipAccept === 'function') {
        this.dc.sipAccept();
        console.log('[OzonetelSDKCore] Inbound call connected via sipAccept()', {
          ucid: callData?.ucid,
          callerId: callData?.callerId || callData?.customerNumber,
        });
        return true;
      }
      if (typeof this.dc.handleSIPAutoAnswer === 'function') {
        this.dc.handleSIPAutoAnswer();
        console.log('[OzonetelSDKCore] Inbound call connected via handleSIPAutoAnswer()', {
          ucid: callData?.ucid,
        });
        return true;
      }
      if (typeof this.dc.answerSIPCall === 'function') {
        this.dc.answerSIPCall();
        return true;
      }
    } catch (e: any) {
      const msg = e?.message || String(e);
      if (!/NO_ACTIVE_SIP|not connected|WAITING|SIP Service/i.test(msg)) {
        console.warn('[OzonetelSDKCore] sipAccept:', msg);
      }
    }
    return false;
  }

  /** Retries sipAccept after toolbar custData — SIP INVITE may lag screen-pop response. */
  private async runSipAcceptAfterToolbar(callData: any): Promise<void> {
    const delays = [0, 400, 800, 1500, 2500, 4000];
    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) {
        await new Promise((r) => setTimeout(r, delays[i] - delays[i - 1]));
      }
      if (this.trySipAcceptOnce(callData)) {
        this.scheduleInboundConnectUnhold(callData, 'sip-toolbar');
        return;
      }
    }
    console.warn('[OzonetelSDKCore] toolbar custData OK but sipAccept did not connect — check mic/WebRTC');
    this.scheduleInboundConnectUnhold(callData, 'sip-toolbar-fallback');
  }

  /**
   * Full inbound auto-answer pipeline: toolbar GET (optional) then sipAccept retries.
   * Controlled by shouldAutoAnswerOzonetelInbound / useToolbarInboundPick / useDigitalConsoleSipForInboundAudio.
   */
  private async autoAnswerInboundCall(callData: any): Promise<void> {
    if (!shouldAutoAnswerOzonetelInbound()) {
      return;
    }
    const inbound =
      this.isInboundSdkCall(callData) ||
      this.agentState.mode === OZONETEL_INBOUND_AGENT_MODE;
    if (!inbound) {
      return;
    }
    const answerUcid = this.extractUcid(callData) || '_unknown';
    if (!this.canPromoteCallToActive(answerUcid === '_unknown' ? '' : answerUcid)) {
      return;
    }
    if (this.inboundAutoAnswerUcids.has(answerUcid)) {
      return;
    }
    this.inboundAutoAnswerUcids.add(answerUcid);

    const sipConnected = (): boolean =>
      !this.dc ||
      typeof this.dc.isSIPConnected !== 'function' ||
      this.dc.isSIPConnected();

    const useToolbar = useToolbarInboundPick();
    const useSip =
      !!this.dc &&
      this.isDigitalConsoleSipEnabled() &&
      useDigitalConsoleSipForInboundAudio();

    console.log('[OzonetelSDKCore] autoAnswerInboundCall', {
      useToolbar,
      useSip,
      hasCallData: !!callData,
      mode: this.agentState.mode,
    });

    try {
      const retryMs = [0, 300, 600, 1000, 1500, 2500];
      for (let i = 0; i < retryMs.length; i++) {
        if (this.inboundToolbarPickBlocked) {
          break;
        }
        if (retryMs[i] > 0) {
          await new Promise((r) => setTimeout(r, retryMs[i] - retryMs[i - 1]));
        }
        if (useToolbar) {
          if (await this.pickInboundViaToolbar(callData)) {
            if (useSip) {
              await this.runSipAcceptAfterToolbar(callData);
            } else {
              this.scheduleInboundConnectUnhold(callData, 'toolbar');
            }
            return;
          }
        }
        if (useSip && sipConnected() && this.trySipAcceptOnce(callData)) {
          this.scheduleInboundConnectUnhold(callData, 'sip');
          return;
        }
      }
      if (useToolbar && !this.inboundToolbarPickBlocked) {
        if (await this.pickInboundViaToolbar(callData)) {
          if (useSip) {
            await this.runSipAcceptAfterToolbar(callData);
          } else {
            this.scheduleInboundConnectUnhold(callData, 'toolbar-late');
          }
          return;
        }
      }
      if (useSip && this.trySipAcceptOnce(callData)) {
        this.scheduleInboundConnectUnhold(callData, 'sip-late');
        return;
      }
      if (!this.inboundToolbarPickBlocked) {
        console.warn(
          '[OzonetelSDKCore] Could not auto-pick inbound — run CloudAgent login for toolbar cookies or enable SIP/WebRTC'
        );
      }
    } finally {
      this.inboundAutoAnswerUcids.delete(answerUcid);
    }
  }

  /** OneCxi app_to_app: WebCall event then webstartStreaming sends Accept (non–DC-SIP inbound). */
  private bindOneCxiInboundWebCallAccept(): void {
    if (this.oneCxiWebCallListenerBound || !shouldAutoAnswerOzonetelInbound()) {
      return;
    }
    this.oneCxiWebCallListenerBound = true;
    window.addEventListener('WebCall', (event: any) => {
      if (!this.oneCXI || this.isDigitalConsoleSipEnabled()) {
        return;
      }
      const detail = event?.detail || {};
      const phone =
        detail.callerId ||
        detail.customerNumber ||
        detail.phoneNumber ||
        detail.did;
      if (!phone || typeof this.oneCXI.webstartStreaming !== 'function') {
        return;
      }
      try {
        this.oneCXI.webstartStreaming(String(phone), {
          callType: 'Ozone_web_client',
          isCustomer: false,
        });
        this.isStreaming = true;
        console.log('[OzonetelSDKCore] Inbound picked via OneCxi WebCall Accept', { phone });
      } catch (e: any) {
        console.warn('[OzonetelSDKCore] OneCxi WebCall Accept failed:', e?.message || e);
      }
    });
  }

  private async startOneCxiOutboundStream(callData: any): Promise<void> {
    const eventDid =
      callData.did ||
      callData.DID ||
      callData.phoneNumber ||
      callData.callerId ||
      callData.callerID ||
      callData.customerNumber;
    const didToUse = eventDid || this.oneCxiPreferredDid;
    const oneCxiUserName = this.config?.userName || this.config?.customer;

    if (!didToUse || typeof OneCxi === 'undefined') {
      return;
    }
    if (!this.oneCXI) {
      this.oneCXI = new OneCxi();
    }
    if (!this.oneCxiInitialized && oneCxiUserName) {
      await this.initializeOneCxi(oneCxiUserName);
    }
    console.log('[OzonetelSDKCore] OneCxi outbound stream (manual/outbound leg)', { did: didToUse });
    this.oneCXI.startStreaming(didToUse);
    this.isStreaming = true;
    this.oneCxiInitialized = true;
  }

  // --- DigitalConsole WS event handlers (registered once per dc instance) ---
  //
  // DC routes WS events through onEventName(fn) registrars — NOT simple property assignment.
  // See registerDigitalConsoleHandler(). Key events:
  //   onNewCall / onBusyAgent — ring + connect
  //   onAgentCallDisconnected / onDropCall — disconnect (local or remote)
  //   onAgentRelease — agent returned to IDLE after call ends
  //   onCallDetails(endTime) — CDR with end timestamp

  private setupDefaultEventHandlers(): void {
    if (!this.dc) return;
    if (this.defaultHandlersRegistered) {
      return;
    }
    this.defaultHandlersRegistered = true;

    console.log('[OzonetelSDKCore] Setting up default event handlers');
    this.bindOneCxiInboundWebCallAccept();

    this.registerDigitalConsoleHandler('onNewCall', async (data: any) => {
      console.log('[OzonetelSDKCore] onNewCall event received:', data);
      // Wrap-up guard: no new inbound leg until disposition submitted (see wrapUpPending).
      if (this.wrapUpPending) {
        console.log('[OzonetelSDKCore] Ignoring onNewCall — wrap-up disposition pending');
        return;
      }
      if (!this.acceptDcCallEvent(data)) {
        return;
      }
      this.latestCallSignals = { ...(this.latestCallSignals || {}), ...(data || {}) };
      this.markActiveCall(data);
      void this.seedMdlSessionFromSdk(data);
      this.emitCallEvent('NEW_CALL', data);

      if (this.isOutboundSdkLeg(data)) {
        if (this.isDigitalConsoleSipEnabled()) {
          this.trySipAcceptOnce(data);
          void this.autoAnswerOutboundSipCall(data);
        } else {
          try {
            await this.startOneCxiOutboundStream(data);
          } catch (error) {
            console.error('[OzonetelSDKCore] OneCxi outbound stream error:', error);
          }
        }
      } else {
        this.scheduleInboundToolbarPick('onNewCall', data);
        const inbound = this.isInboundSdkCall(data);
        const dcSip = this.isDigitalConsoleSipEnabled();
        if (inbound && dcSip && useDigitalConsoleSipForInboundAudio()) {
          console.log(
            '[OzonetelSDKCore] Inbound on DC SIP — audio via sipAccept (skip OneCxi outbound stream)'
          );
        } else if (this.shouldStartOneCxiOutboundStream(data)) {
          try {
            await this.startOneCxiOutboundStream(data);
          } catch (error) {
            console.error('[OzonetelSDKCore] OneCxi outbound stream error:', error);
          }
        }
      }
    });

    this.registerDigitalConsoleHandler('onAgentCallAnswered', (data: any) => {
      console.log('[OzonetelSDKCore] onAgentCallAnswered:', data);
      if (!this.acceptDcCallEvent(data)) {
        return;
      }
      this.latestCallSignals = { ...(this.latestCallSignals || {}), ...(data || {}) };
      this.markActiveCall(data);
      this.inboundToolbarPickBlocked = false;
      this.inboundToolbarPickAttempts = 0;
      this.agentState.status = 'BUSY';
      this.emitAgentState();
      this.emitCallEvent('BUSY', data);
      if (this.isOutboundSdkLeg(data) && this.isDigitalConsoleSipEnabled()) {
        this.trySipAcceptOnce(data);
        this.clearManualDialPending();
      }
      if (data && this.shouldStartOneCxiOutboundStream(data)) {
        void this.startOneCxiOutboundStream(data);
      }
      if (this.isTransferLegData(data)) {
        void this.autoUnholdTransferLeg(data);
      }
    });

    // Primary remote-hangup events — fired when the customer disconnects
    this.registerDigitalConsoleHandler('onAgentCallDisconnected', (data: any) => {
      console.log('[OzonetelSDKCore] onAgentCallDisconnected:', data);
      this.handleRemoteDisconnect(data, 'onAgentCallDisconnected');
    });

    this.registerDigitalConsoleHandler('onAgentRelease', (data: any) => {
      console.log('[OzonetelSDKCore] onAgentRelease:', data);
      if (this.rearmInProgress && !this.callSessionActive && this.agentState.status !== 'BUSY') {
        return;
      }
      this.handleRemoteDisconnect(data, 'onAgentRelease');
    });

    this.registerDigitalConsoleHandler('onAgentReleaseWOACW', (data: any) => {
      console.log('[OzonetelSDKCore] onAgentReleaseWOACW:', data);
      if (this.rearmInProgress && !this.callSessionActive && this.agentState.status !== 'BUSY') {
        return;
      }
      this.handleRemoteDisconnect(data, 'onAgentReleaseWOACW');
    });

    // Some tenants fire inbound ring via onPerformCallEvent instead of onNewCall.
    this.registerDigitalConsoleHandler('onPerformCallEvent', (data: any) => {
      console.log('[OzonetelSDKCore] onPerformCallEvent:', data);
      // Wrap-up guard: same as onNewCall — block ring/connect during ACW.
      if (this.wrapUpPending) {
        console.log('[OzonetelSDKCore] Ignoring onPerformCallEvent — wrap-up disposition pending');
        return;
      }
      if (!this.acceptDcCallEvent(data)) {
        return;
      }
      this.latestCallSignals = { ...(this.latestCallSignals || {}), ...(data || {}) };
      void this.seedMdlSessionFromSdk(data);
      const type = String(data?.callType ?? data?.calltype ?? data?.type ?? '').toLowerCase();
      const status = String(data?.status ?? data?.callStatus ?? data?.callState ?? '').toLowerCase();
      const isTransferLeg =
        type.includes('transfer') || data?.isTransfer === true || data?.transfer === true;
      const isOutboundLeg =
        this.isOutboundSdkLeg(data) ||
        type.includes('manual') ||
        type.includes('outbound') ||
        type.includes('preview') ||
        type.includes('progressive') ||
        type.includes('predictive');
      const isConnectedLeg =
        status.includes('connect') ||
        status.includes('answer') ||
        status.includes('busy') ||
        status.includes('live') ||
        status.includes('bridge') ||
        type.includes('answered') ||
        type.includes('connect') ||
        type.includes('busy');
      if (isTransferLeg || this.isInboundSdkCall(data) || isOutboundLeg) {
        this.markActiveCall(data);
        if (isConnectedLeg) {
          this.agentState.status = 'BUSY';
          this.emitAgentState();
          this.emitCallEvent('BUSY', data);
          if (isOutboundLeg && this.isDigitalConsoleSipEnabled()) {
            this.trySipAcceptOnce(data);
            void this.autoAnswerOutboundSipCall(data);
          } else if (isOutboundLeg && this.shouldStartOneCxiOutboundStream(data)) {
            void this.startOneCxiOutboundStream(data);
          }
          if (isTransferLeg) {
            void this.autoUnholdTransferLeg(data);
          }
        } else {
          this.emitCallEvent('NEW_CALL', data);
          if (isOutboundLeg && this.isDigitalConsoleSipEnabled()) {
            this.trySipAcceptOnce(data);
            void this.autoAnswerOutboundSipCall(data);
          }
        }
      }
      if (!isOutboundLeg) {
        this.scheduleInboundToolbarPick('onPerformCallEvent', data);
      }
    });

    this.registerDigitalConsoleHandler('onBusyAgent', (data: any) => {
      console.log('[OzonetelSDKCore] onBusyAgent event:', data);
      if (!this.acceptDcCallEvent(data)) {
        return;
      }
      this.latestCallSignals = { ...(this.latestCallSignals || {}), ...(data || {}) };
      void this.seedMdlSessionFromSdk(data);
      if (!this.isOutboundSdkLeg(data)) {
        this.scheduleInboundToolbarPick('onBusyAgent', data);
      } else if (this.isDigitalConsoleSipEnabled()) {
        this.trySipAcceptOnce(data);
      }
      this.markActiveCall(data);
      this.agentState.status = 'BUSY';
      this.emitAgentState();
      this.emitCallEvent('BUSY', data);
      if (this.isTransferLegData(data)) {
        void this.autoUnholdTransferLeg(data);
      }
    });

    // Agent-initiated hangup from DC UI or dc.hangupCall()
    this.registerDigitalConsoleHandler('onDropCall', (data: any) => {
      console.log('[OzonetelSDKCore] onDropCall event:', data);
      if (this.callSessionActive || this.hasActiveCallSession()) {
        this.handleCallEnded({ ...(data || {}), source: 'DigitalConsole', event: 'onDropCall' });
      }
    });

    this.registerDigitalConsoleHandler('onDecline', (data: any) => {
      console.log('[OzonetelSDKCore] onDecline event:', data);
      if (this.callSessionActive) {
        this.handleCallEnded({ ...(data || {}), source: 'DigitalConsole', event: 'onDecline' });
      }
    });

    this.registerDigitalConsoleHandler('onCallDetails', (data: any) => {
      console.log('[OzonetelSDKCore] onCallDetails event:', data);

      if (data && !data.endTime && this.acceptDcCallEvent(data)) {
        this.latestCallSignals = { ...(this.latestCallSignals || {}), ...data };
        if (data.ucid || data.monitorUcid) {
          this.scheduleInboundToolbarPick('onCallDetails', data);
        }
      }

      // CRITICAL: Check if call has ended (endTime is present)
      // When receiver hangs up, onCallDetails is called with endTime indicating call ended
      if (data && data.endTime) {
        console.log('[OzonetelSDKCore] Call details show call has ended (endTime present):', {
          endTime: data.endTime,
          startTime: data.startTime,
          type: data.type,
          agentId: data.agentId,
          agentAnswerTime: data.agentAnswerTime
        });

        void this.teardownActiveCall(data, 'onCallDetails');
        return;
      }

      // Check if call details contain hold status information (only if call is still active)
      if (data) {
        console.log('[OzonetelSDKCore] onCallDetails hold-related fields:', {
          holdCall: data.holdCall,
          isOnHold: data.isOnHold,
          holdStatus: data.holdStatus,
          isHeld: data.isHeld,
          onHold: data.onHold,
          holdState: data.holdState,
          callStatus: data.callStatus,
          status: data.status,
          callState: data.callState,
          fullData: data
        });
      }
    });

    // OneCxi WebRTC path (non–DC-SIP outbound/inbound) — window events for call termination
    window.addEventListener('Ended', (event: any) => {
      console.log('[OzonetelSDKCore] OneCxi Ended event - Call ended', event);
      this.handleCallEnded({ source: 'OneCxi', event: 'Ended', detail: event?.detail });
    });

    window.addEventListener('Disconnected', (event: any) => {
      console.log('[OzonetelSDKCore] OneCxi Disconnected event - Call disconnected', event);
      this.handleCallEnded({ source: 'OneCxi', event: 'Disconnected', detail: event?.detail });
    });

    window.addEventListener('callTerminated', (event: any) => {
      console.log('[OzonetelSDKCore] OneCxi callTerminated event - Call terminated', event);
      this.handleCallEnded({ source: 'OneCxi', event: 'callTerminated', detail: event?.detail, reason: event?.detail?.reason });
    });

    window.addEventListener('Started', () => {
      console.log('[OzonetelSDKCore] OneCxi Started event - WebRTC audio stream has started');
    });

    // Listen for SIP_CALL_ENDED event from DigitalConsole SDK
    // This event is fired when SIP call ends (receiver hangs up)
    window.addEventListener('SIP_CALL_ENDED', (event: any) => {
      console.log('[OzonetelSDKCore] SIP_CALL_ENDED event received - SIP call has ended', event);
      if (this.hasActiveCallSession()) {
        this.handleCallEnded({
          source: 'DigitalConsole',
          event: 'SIP_CALL_ENDED',
          detail: event?.detail,
          timestamp: Date.now()
        });
      }
    });

    window.addEventListener('SIP_DISCONNECTED', (event: any) => {
      console.log('[OzonetelSDKCore] SIP_DISCONNECTED event received', event);
      if (this.hasActiveCallSession()) {
        this.handleCallEnded({
          source: 'DigitalConsole',
          event: 'SIP_DISCONNECTED',
          detail: event?.detail,
          timestamp: Date.now()
        });
      }
    });

    // Backup path if DC events omit ucid until getCallData() updates.
    this.startInboundCallPoll();
    console.log('[OzonetelSDKCore] Default event handlers setup complete');
  }

  setupEventHandlers(handlers: {
    onNewCall?: (data: any) => void;
    onBusyAgent?: (data: any) => void;
    onDropCall?: (data: any) => void;
    onCallDetails?: (data: any) => void;
  }): void {
    if (!this.dc) return;

    if (handlers.onNewCall) {
      this.registerDigitalConsoleHandler('onNewCall', handlers.onNewCall);
    }
    if (handlers.onBusyAgent) {
      this.registerDigitalConsoleHandler('onBusyAgent', handlers.onBusyAgent);
    }
    if (handlers.onDropCall) {
      this.registerDigitalConsoleHandler('onDropCall', handlers.onDropCall);
    }
    if (handlers.onCallDetails) {
      this.registerDigitalConsoleHandler('onCallDetails', handlers.onCallDetails);
    }
  }

  /**
   * Registers a handler on DigitalConsole's internal WS event bus.
   * MUST call dc.onEventName(fn) — the registrar wires fn to #J(eventKey, fn).
   * Direct assignment (dc.onEventName = fn) bypasses the bus and events never arrive.
   */
  private registerDigitalConsoleHandler(
    eventName: string,
    handler: (data: any) => void | Promise<void>
  ): void {
    if (!this.dc) {
      return;
    }

    const wrapped = (data: any) => {
      void Promise.resolve(handler(data)).catch((e) =>
        console.warn(`[OzonetelSDKCore] ${eventName} handler error`, e)
      );
    };

    const sdkRegistrar = this.dc[eventName];
    if (typeof sdkRegistrar === 'function' && sdkRegistrar.length >= 1) {
      try {
        sdkRegistrar.call(this.dc, wrapped);
        return;
      } catch (e) {
        console.warn(`[OzonetelSDKCore] DC registrar failed for ${eventName}`, e);
      }
    }

    this.dc[eventName] = wrapped;
  }
}
